/* eslint-disable no-undef */
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Prompt, Redirect, useHistory, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import format from 'date-fns/format';
import { Dialog, DialogContent, DialogTitle, Modal } from '@mui/material';
import {
  WorkflowPageHeader,
  WorkflowNotice,
  WorkflowAssistantHeader,
  WorkflowCreditWarning,
  WorkflowProgressHeader,
  WorkflowProgressTimeline,
  WorkflowComposer,
  WorkflowStreamingResponse,
  WorkflowSequentialReveal,
  useWorkflowChatScroll,
  useScrollWorkflowIntoView,
} from 'components/Workflows/StandardWorkflowLayout';
import type {
  WorkflowProgressStep,
  WorkflowStepStatus,
} from 'components/Workflows/StandardWorkflowLayout';
import CreditMeterButton from 'pages/credit-plans/CreditMeterButton';
import { useWorkflowCredits } from 'hooks/useWorkflowCredits';
import TrialRunUsedPanel from 'components/Workflows/TrialRunUsedPanel';
import CasesAPI from 'api/CasesAPI';
import CreditsAPI from 'api/CreditsAPI';
import { screeningTicketBeneficialOwnershipPDF } from 'components/Common/EntityTicketPdf';
import { loadDraft, registerRecoverable } from 'services/sessionRecovery';
import CreateTicketWizard from 'components/Wizard/CreateTicketWizard';
import { GlobalContext } from 'providers/GlobalProvider';
import RequiredLicense from 'pages/assign/RequiredLicense';
import { LicenseType } from 'pages/assign/LicenseBadge';
import KeycloakService from 'services/KeycloakService';
import { useLogAppAction } from 'hooks/useLogAppAction';
import { didSecondLayerRunFromState } from 'utils/beneficialOwnershipLayers';
import { buildOwnershipGraph } from 'utils/beneficialOwnershipGraph';

type BOStage =
  | 'discovery'
  | 'affiliated_screening'
  | 'boi_peps'
  | 'ownership'
  | 'second_layer'
  | 'third_layer'
  | 'sanctions'
  | 'screening'
  | 'analysis'
  | 'followup'
  | 'final';

type BOEventName =
  | 'followup'
  | 'step_started'
  | 'step_completed'
  | 'phase_started'
  | 'phase_completed'
  | 'phase_paused'
  | 'pivot_suggested'
  | 'token_delta'
  | 'tool_activity'
  | 'heartbeat'
  | 'final'
  | 'notify_scheduled'
  | 'workflow_error';

type StepStatusValue =
  | 'pending'
  | 'running'
  | 'complete'
  | 'done'
  | 'skipped'
  | 'error';

interface BOFollowupItem {
  question: string;
  answer?: string | null;
}

interface BOStepStatus {
  status?: StepStatusValue;
  complete?: boolean;
  output?: unknown;
  error?: string;
}

interface BOStreamEvent {
  message: string;
  bo_id?: string;
  stage?: BOStage;
  event?: BOEventName;
  current_step?: string;
  followup_items?: BOFollowupItem[];
  steps_status?: Record<string, BOStepStatus>;
  phase_status?: Record<string, string>;
  control?: BOControl;
  workflow_state?: Record<string, unknown>;
  data?: any;
}

// Workflow-control payload attached to every backend frame (see spec §6). Drives
// the pause buttons and, at a pause, awaiting_decision/available_actions.
interface BOControl {
  run_mode?: 'step' | 'auto';
  active_phase?: string | null;
  next_phase?: string | null;
  awaiting_decision?: boolean;
  available_actions?: string[];
  phase_order?: string[];
  phase_labels?: Record<string, string>;
  // Whether a deeper second-layer search was requested — gates the ownership
  // entity drill-down (tickable) vs a read-only ownership result card.
  second_layer?: boolean;
}

interface BOMessage {
  id: number;
  sender: 'user' | 'bot';
  text: string | JSX.Element;
  type?:
    | 'status'
    | 'question'
    | 'answer'
    | 'final'
    | 'error'
    | 'control'
    | 'agent_result'
    | 'layer_picklist'
    | 'jurisdiction_confirm'
    | 'thinking';
  variant?: 'header' | 'search';
  stage?: BOStage;
  data?: any;
  thinking?: BOThinking;
}

// One line in a phase's "Thinking" transcript, tagged with its place in the
// phase -> group -> step -> finding hierarchy (ported from the Export workflow's
// EarThoughtStep; see EARChatUI.tsx). `phase` = the phase's own overview line (its
// step_started message); `group` = a sub-stage heading, e.g. one of the two BITE
// record checks; `step` = a fine-grained line beneath a group (a query issued, a
// source reviewed); `finding` = a DETERMINATION, the RESULT of a step, rendered with
// more contrast so the user sees what was FOUND and not only what is being done.
// The backend tags group/finding lines via `data.level` on a tool_activity frame.
type BOThoughtLevel = 'phase' | 'group' | 'step' | 'finding';

interface BOThoughtStep {
  text: string;
  level: BOThoughtLevel;
}

// One "Thinking" transcript for a single pipeline phase/agent.
//
// Lines only ever APPEND: every narrated line stays on screen for the rest of the
// phase, so the block visibly grows while the phase runs and settles into a complete,
// readable account of what that agent did. (It previously held a flat `searches` list
// and rendered exactly ONE line at a time, which meant a phase's own narration
// overwrote itself; long silent phases then had to FAKE movement by cycling an index
// through the list on a timer. Appending removes the need for that illusion — see
// `heartbeatLine` for the honest replacement.)
//
// `header` is the phase's step_started line, kept separately because it doubles as
// the block's uppercase title. `thinkingText` is the streamed model reasoning
// (token_delta kind='thinking', final-analysis phase only). Phases run strictly
// sequentially, so at most one transcript is `running` at a time.
interface BOThinking {
  agentKey: string;
  header: string;
  steps: BOThoughtStep[];
  thinkingText: string;
  // How many heartbeat "still working" lines this phase has already appended, so the
  // sequence advances and then STOPS rather than repeating forever (see HEARTBEAT_LINES).
  heartbeats: number;
  running: boolean;
  // Whether this phase actually FINISHED, as opposed to merely stopping (the stream
  // ended, or it paused to ask the user a question). Only a genuine completion earns
  // the "Done" row: discovery asks several questions before it is finished, and marking
  // each intake turn "Done" claimed a completion that had not happened. Set by a
  // step_completed / phase_completed for the phase; `running` going false does NOT
  // imply it.
  completed: boolean;
  startedAt: number;
  elapsedMs: number;
}

// One record an agent returned, shown in its completion bubble and sidebar card.
// `group` optionally sub-sections a list (e.g. Records Lookup → sanctions-affiliated
// vs BOI/PEP).
interface Finding {
  title: string;
  detail?: string;
  url?: string;
  group?: string;
}

type IntakeStep = 'entity' | 'country' | 'second_layer' | 'ready';

interface AgentStatus {
  key: string;
  label: string;
  status: 'pending' | 'running' | 'done' | 'skipped' | 'error';
}

const BO_CHAT_RECOVERY_KEY = 'workflow-beneficial-ownership-chat';

// isStreaming, isSaving, and every modal/UI-only flag are deliberately left
// out: after a forced re-login redirect there's no live stream or in-flight
// save left to represent, and restoring "a modal was open" would be
// meaningless on a fresh mount.
interface BOChatRecoverableState {
  messages: BOMessage[];
  input: string;
  entityName: string;
  country: string;
  secondLayer: boolean;
  intakeStep: IntakeStep;
  boId: string;
  workflowState: Record<string, unknown>;
  followupItems: BOFollowupItem[];
  agentStatuses: AgentStatus[];
  agentFindings: Record<string, Finding[]>;
  agentResultText: Record<string, string>;
  agentReport: any;
  pendingDecision: BOControl | null;
  selectedEntities: string[];
  layerOfferYes: Record<number, boolean>;
  confirmChosen: Record<number, string>;
  stepsStatus: Record<string, BOStepStatus>;
  currentStep: string;
  activeAgentKey: string;
  finalData: any;
  streamStage: number;
  ticketId: string;
  newTicketName: string;
  shareEmail: string[];
}

// A message's `text` can in principle be a JSX.Element, which can't survive a
// JSON round-trip (and wouldn't mean anything after a fresh page load even if
// it did) -- drop any that aren't plain strings rather than let JSON.stringify
// throw or silently corrupt the draft.
const sanitizeBoMessagesForRecovery = (messages: BOMessage[]): BOMessage[] =>
  messages.filter((message) => typeof message.text === 'string');

// User-facing copy for ANY workflow failure. The real error (API 400, stack trace,
// network detail) stays in the backend logs / browser console — we never surface raw
// error text in the chat, since it's noise to the user and can leak internal detail.
const WORKFLOW_ERROR_MESSAGE =
  'Something went wrong and this investigation could not be completed. Please start a new investigation and try again. If the problem continues, contact support.';

// Transient capacity error (the model service was briefly overloaded — e.g. while
// another investigation runs in the background). Nothing was lost; the user just needs
// to retry in a moment. Kept distinct from the generic failure copy so it doesn't tell
// the user to start over.
const SERVICE_BUSY_MESSAGE =
  'The AI service is briefly busy right now (this is more likely when another investigation is running in the background). Nothing was lost — please wait a few moments and try again.';

const apiUrl = process.env.REACT_APP_PYAPI_URL as string;

const BENEFICIAL_OWNERSHIP_CREDIT_COST = 20; // charged on completion; matches the pricing page
const leaveWarningStorageKey =
  'beneficialOwnershipWorkflowLeaveWarningDismissed';

// The events the backend can legitimately close a stream on: a finished
// investigation, a checkpoint pause, an error, a question back to the user, a
// pivot suggestion — plus step_completed, which is how _run_followup ends a
// follow-up answer. Anything else as the last frame means the stream was cut.
const TURN_END_EVENTS = new Set<BOEventName>([
  'final',
  'workflow_error',
  'phase_paused',
  'followup',
  'pivot_suggested',
  'step_completed',
  // The detached "run & notify me" acknowledgement cleanly ends the stream: the run
  // continues server-side and the user will be emailed, so the client stops here.
  'notify_scheduled',
]);

// How many steps the backend currently considers finished. Echoed on every frame,
// so it works as a resume-proof progress mark: replaying settled phases reports
// the same count, only genuinely new work raises it.
const countSettledSteps = (event: BOStreamEvent): number =>
  Object.values(event.steps_status || {}).filter(
    (step) =>
      step?.complete ||
      step?.status === 'complete' ||
      step?.status === 'done' ||
      step?.status === 'skipped',
  ).length;

// One card per real agent (see docs/bo-workflow-interactivity-spec.md §4), so the
// sidebar shows what each agent is doing individually rather than the old grouped
// buckets. STEP_TO_AGENT / AGENT_STEP_ORDER are 1:1 with the backend step keys.
// One card per real agent. Discovery is intentionally NOT here — it's an intake step
// with nothing report-worthy — but it still runs (its questions appear in chat) and
// its step keys remain in the maps below so events still process.
const AGENTS: AgentStatus[] = [
  // Affiliated screening + BOI/PEP lookup are presented as one "BITE Records Look-up"
  // agent (they run in parallel); the source distinction is carried in the results.
  { key: 'records', label: 'BITE Records Look-up', status: 'pending' },
  { key: 'ownership', label: 'Ownership Research', status: 'pending' },
  { key: 'second_layer', label: 'Second-Layer Search', status: 'pending' },
  { key: 'third_layer', label: 'Third-Layer Search', status: 'pending' },
  { key: 'sanctions', label: 'Sanctions & Adverse Media', status: 'pending' },
  { key: 'screening', label: 'BITE Sanctions Screening', status: 'pending' },
  { key: 'analysis', label: 'Final Analysis', status: 'pending' },
];

const STEP_TO_AGENT: Record<string, string> = {
  'bo.discovery': 'discovery',
  'bo.affiliated_screening': 'records',
  'bo.boi_peps_lookup': 'records',
  'bo.ownership_research': 'ownership',
  'bo.second_layer': 'second_layer',
  'bo.third_layer': 'third_layer',
  'bo.sanctions_web': 'sanctions',
  'bo.sanctions_screening': 'screening',
  'bo.final_analysis': 'analysis',
  'bo.followup': 'analysis',
};

const AGENT_STEP_ORDER: Record<string, string[]> = {
  discovery: ['bo.discovery'],
  records: ['bo.affiliated_screening', 'bo.boi_peps_lookup'],
  ownership: ['bo.ownership_research'],
  second_layer: ['bo.second_layer'],
  third_layer: ['bo.third_layer'],
  sanctions: ['bo.sanctions_web'],
  screening: ['bo.sanctions_screening'],
  analysis: ['bo.final_analysis', 'bo.followup'],
};

// Agents that get their own `<agent>_completed` app-action record. Discovery is an
// intake step with no card, and `analysis` is covered by the workflow-level
// `completed` record (it also hosts the follow-up step), so neither is listed.
const SECTION_LOG_AGENTS = new Set([
  'records',
  'ownership',
  'second_layer',
  'third_layer',
  'sanctions',
  'screening',
]);

const STEP_LABELS: Record<string, string> = {
  'bo.discovery': 'Beneficial ownership discovery',
  'bo.affiliated_screening': 'BITE record checks',
  'bo.boi_peps_lookup': 'BOI and PEPs lookup',
  'bo.ownership_research': 'Ownership and corporate structure research',
  'bo.second_layer': 'Second-layer connection search',
  'bo.third_layer': 'Third-layer connection search',
  'bo.sanctions_web': 'Sanctions and adverse media research',
  'bo.sanctions_screening': 'BITE sanctions screening',
  'bo.final_analysis': 'Final ownership risk analysis',
  'bo.followup': 'Follow-up analysis',
};

const STAGE_TO_AGENT: Record<string, string> = {
  discovery: 'discovery',
  affiliated_screening: 'records',
  boi_peps: 'records',
  ownership: 'ownership',
  second_layer: 'second_layer',
  third_layer: 'third_layer',
  sanctions: 'sanctions',
  screening: 'screening',
  analysis: 'analysis',
  followup: 'analysis',
  final: 'analysis',
};

// Short uppercase title for a phase's Thinking transcript, matching the sidebar
// agent-card labels so both name the phase the same way (mirrors the Export
// workflow's EXPORT_PHASE_CHAT_HEADING).
const AGENT_THOUGHT_HEADING: Record<string, string> = {
  discovery: 'Discovery',
  records: 'BITE Records Look-up',
  ownership: 'Ownership Research',
  second_layer: 'Second-Layer Search',
  third_layer: 'Third-Layer Search',
  sanctions: 'Sanctions & Adverse Media',
  screening: 'BITE Sanctions Screening',
  analysis: 'Final Analysis',
};

// Appended, one at a time, when the backend sends a `heartbeat` (a stage that has gone
// wire-silent for _HEARTBEAT_INTERVAL, default 10s). Web searches run inside
// Anthropic's server loop and the final report generation emits nothing at all, so a
// phase can legitimately have nothing to say for a minute or more.
//
// This is the honest replacement for the old timer-driven line rotation: each line is
// appended once, in order, and the sequence STOPS at the end — so a long quiet phase
// shows a bounded, truthful "still working" tail (never more lines than there are here)
// and then rests on the pulsing "Thinking…" row, rather than inventing movement by
// re-cycling narration the agent already finished with.
const HEARTBEAT_LINES: Record<string, string[]> = {
  ownership: [
    'Still searching for ownership records…',
    'Reading through the sources found so far…',
    'Piecing together the ownership chain…',
  ],
  second_layer: [
    'Still searching for further connections…',
    'Reading through the sources found so far…',
  ],
  third_layer: [
    'Still searching for further connections…',
    'Reading through the sources found so far…',
  ],
  sanctions: [
    'Still checking sanctions lists and news coverage…',
    'Reading through the sources found so far…',
  ],
  records: ['Still working through the record matches…'],
  screening: ['Still comparing the names against BITE records…'],
  // Reached only after the paced narration has run out (the backend releases those a
  // line at a time throughout the report call and ends on "Writing the consolidated
  // risk report"), so these continue that thought rather than starting a new one.
  analysis: [
    'Still writing — this is the longest step…',
    'Almost there…',
  ],
};
const HEARTBEAT_LINES_DEFAULT = ['Still working…'];

const safeUrl = (url?: string) =>
  typeof url === 'string' && /^https?:\/\//i.test(url) ? url : '';

// Every phase's step_completed message reads "<Phase> complete — <what was found>".
// This pulls out the "<what was found>" half, which is the phase's DETERMINATION: it
// both closes that phase's Thinking transcript as a finding and fills the sidebar
// card's note. Falls back to the whole message when there's no dash.
const phaseSummaryNote = (raw: unknown) => {
  const text = String(raw || '');
  const dash = text.indexOf('—');
  return (dash >= 0 ? text.slice(dash + 1) : text).trim();
};

const statusFromStep = (step: BOStepStatus): AgentStatus['status'] => {
  if (step.status === 'error') {
    return 'error';
  }

  if (step.complete || step.status === 'complete' || step.status === 'done') {
    return 'done';
  }

  if (step.status === 'skipped') {
    return 'skipped';
  }

  if (step.status === 'running') {
    return 'running';
  }

  return 'pending';
};

const strongerStatus = (
  previous: AgentStatus['status'],
  incoming: AgentStatus['status'],
) => {
  if (incoming === 'running' && previous !== 'error') {
    return 'running';
  }

  const rank = { pending: 0, running: 1, skipped: 2, done: 2, error: 3 };
  return rank[incoming] >= rank[previous] ? incoming : previous;
};

const getStepLabel = (stepKey?: string) =>
  stepKey ? STEP_LABELS[stepKey] || stepKey.replace(/^bo\./, '') : '';

const getStatusText = (status: AgentStatus['status']) => {
  switch (status) {
    case 'running':
      return 'In progress...';
    case 'done':
      return 'Complete';
    case 'skipped':
      return 'Skipped';
    case 'error':
      return 'Needs attention';
    default:
      return 'Awaiting information...';
  }
};

const isThinkingMessage = (message: BOMessage) =>
  message.text === 'Thinking...';

const BiteAIAvatar = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='32'
    height='32'
    viewBox='0 0 32 32'
    fill='none'
  >
    <g clipPath='url(#bo-ai-avatar-clip)'>
      <path
        d='M0 16C0 7.16344 7.16344 0 16 0C24.8366 0 32 7.16344 32 16C32 24.8366 24.8366 32 16 32C7.16344 32 0 24.8366 0 16Z'
        fill='#4574EE'
      />
      <path
        d='M17.5915 13.6496C16.9135 14.4731 15.4893 14.9301 14.5015 14.4289C13.3459 13.8427 13.0921 11.9548 13.5386 10.8594C14.1072 9.47015 16.0287 8.59755 17.2625 9.68387C18.3252 10.6166 18.488 12.5592 17.5897 13.6486L17.5915 13.6496Z'
        fill='#23377D'
      />
      <path
        d='M15.6677 10.1001C15.3016 7.89481 14.9878 5.68156 14.7297 3.46243C14.9183 3.46175 15.1052 3.46004 15.2948 3.45758C16.0181 5.34973 16.5393 7.37302 17.0281 9.33704C16.8184 9.6877 16.6096 10.0412 16.4 10.3918C16.1168 10.3046 15.8828 10.1981 15.6688 10.0983L15.6677 10.1001Z'
        fill='#3865BB'
      />
      <path
        d='M15.9794 5.46298C16.0854 5.80766 16.1883 6.1577 16.2877 6.50568C15.9017 6.45197 15.5028 6.47421 15.1297 6.57564C15.0774 6.19999 15.0286 5.82639 14.979 5.44998C15.3047 5.4141 15.6466 5.4328 15.9812 5.46401L15.9794 5.46298Z'
        fill='#4CC9F0'
      />
      <path
        d='M15.5266 10.5469C15.5252 10.5247 15.5227 10.5042 15.5185 10.4827C15.6634 10.533 15.8015 10.6032 15.9283 10.6931C16.6211 11.1812 16.7127 12.0131 16.4608 12.7753C16.3127 13.2258 15.9697 13.6424 15.5542 13.805C15.4665 12.7229 15.5662 11.6299 15.5277 10.5451L15.5266 10.5469Z'
        fill='#3865BB'
      />
      <path
        d='M14.3623 10.623C14.4416 10.564 14.5248 10.5191 14.6119 10.4837C14.7086 10.4442 14.8098 10.4216 14.9122 10.4093C14.8844 10.4575 14.8687 10.5176 14.872 10.5862C14.9116 11.6691 14.8122 12.7576 14.8973 13.8358C14.782 13.8002 14.6625 13.7431 14.5474 13.6577C14.3375 13.5055 14.1861 13.2847 14.0707 13.0513C13.7178 12.3377 13.6457 11.1503 14.3615 10.6202L14.3623 10.623Z'
        fill='#3865BB'
      />
      <path
        d='M16.9285 12.3923C16.8894 12.5675 16.9025 12.7799 16.9381 12.9577C17.3502 13.1146 17.8135 13.2987 18.2256 13.4557C18.4536 13.1227 18.5659 12.7135 18.541 12.3109C18.063 12.2969 17.4059 12.3868 16.9275 12.3941L16.9285 12.3923Z'
        fill='#4CC9F0'
      />
      <path
        d='M17.3389 11.4576C17.3616 11.5875 17.4327 11.7285 17.5075 11.8384C17.8308 11.8273 18.1973 11.8198 18.5206 11.8087C18.5787 11.5183 18.5398 11.21 18.4079 10.9432C18.0805 11.0687 17.6607 11.317 17.3407 11.4586L17.3389 11.4576Z'
        fill='#3865BB'
      />
      <path
        d='M8.12142 14.8218C6.99677 18.6559 8.45308 22.4149 12.132 24.103C17.2109 26.4326 25.1027 24.9262 26.6314 19.534C26.8551 18.746 26.9039 17.8403 26.6117 17.0999C26.493 16.7979 26.3189 16.5259 26.0793 16.297C25.3812 15.6367 24.3409 15.5482 23.3983 15.3733C22.5909 15.224 21.7738 14.9761 21.1406 14.4509C20.7407 14.12 20.4334 13.6948 20.0867 13.3089C18.5567 11.6132 16.2637 10.0916 13.9694 9.7651C12.1334 9.50314 10.6679 10.4318 9.62315 11.7916C8.93539 12.6856 8.42955 13.7681 8.11963 14.8208L8.12142 14.8218Z'
        fill='white'
      />
      <path
        d='M8.28084 14.4393C7.1562 18.2733 8.6125 22.0324 12.2914 23.7204C17.3704 26.0501 25.2621 24.5436 26.7908 19.1514C27.0146 18.3635 27.0633 17.4578 26.7712 16.7174C26.668 17.3129 26.5826 17.8116 26.498 18.02C25.9471 19.3623 24.9208 20.5207 23.657 21.2323C21.4592 22.4719 18.7318 22.4909 16.3099 21.7787C13.6909 21.0075 11.2184 19.2894 10.1836 16.7647C9.494 15.0826 9.4962 13.2257 9.78257 11.409C9.09481 12.3031 8.58897 13.3856 8.27905 14.4383L8.28084 14.4393Z'
        fill='#EDEDED'
      />
      <path
        d='M26.1648 19.4883C25.1974 19.6492 24.0073 17.2326 24.2273 16.4306C24.3087 16.1369 24.6346 15.9892 24.9344 15.9241C26.0395 15.6855 27.1937 16.0016 27.2202 17.2723C27.2317 17.9127 27.0187 18.583 26.6642 19.1144C26.5119 19.341 26.3423 19.4574 26.1658 19.4865L26.1648 19.4883Z'
        fill='#23377D'
      />
      <path
        d='M10.2253 16.7191C9.72592 17.6625 8.42206 18.3938 7.35249 18.1003C6.10476 17.7563 5.47582 15.9567 5.69509 14.7969C5.97485 13.3218 7.68172 12.0823 9.10912 12.8992C10.3381 13.6017 10.8844 15.4702 10.2225 16.7199L10.2253 16.7191Z'
        fill='#23377D'
      />
      <path
        d='M7.63117 13.6256C6.83174 11.5394 6.08257 9.43214 5.38516 7.30954C5.5696 7.27071 5.75405 7.23188 5.9385 7.19306C7.02494 8.90184 7.93942 10.7805 8.81163 12.6086C8.6766 12.9952 8.54158 13.3818 8.40655 13.7683C8.11055 13.7404 7.86075 13.6819 7.63117 13.6256Z'
        fill='#3865BB'
      />
      <path
        d='M7.0096 9.01929C7.1838 9.33664 7.35311 9.65832 7.51781 9.97973C7.12817 10.0025 6.74202 10.1059 6.39704 10.2784C6.27139 9.92236 6.1478 9.56276 6.02497 9.20597C6.33508 9.1063 6.67647 9.05565 7.0096 9.01929Z'
        fill='#4CC9F0'
      />
      <path
        d='M7.58337 14.0935C7.57733 14.071 7.57204 14.0512 7.5629 14.034C7.71509 14.0552 7.86515 14.0966 8.00564 14.1586C8.78119 14.4992 9.03674 15.2947 8.94266 16.0932C8.8866 16.5659 8.63448 17.0397 8.26026 17.2834C7.95947 16.2402 7.83804 15.1505 7.58234 14.0953L7.58337 14.0935Z'
        fill='#3865BB'
      />
      <path
        d='M6.4554 14.4C6.52176 14.3264 6.59292 14.265 6.67274 14.2134C6.76026 14.1568 6.85438 14.1134 6.95152 14.0814C6.9326 14.1348 6.9308 14.1957 6.94715 14.2623C7.20209 15.3147 7.32277 16.4016 7.62281 17.4419C7.50052 17.4309 7.37397 17.3983 7.24447 17.3379C7.00871 17.2303 6.81551 17.0449 6.65773 16.8395C6.17034 16.2103 5.86105 15.0622 6.45822 14.3993L6.4554 14.4Z'
        fill='#3865BB'
      />
      <path
        d='M9.3215 15.6213C9.31713 15.8022 9.37511 16.0072 9.44374 16.1731C9.87853 16.2454 10.3698 16.3313 10.8046 16.4037C10.9618 16.0323 10.99 15.6079 10.8858 15.2189C10.415 15.2997 9.78801 15.519 9.3215 15.6213Z'
        fill='#4CC9F0'
      />
      <path
        d='M9.53921 14.6222C9.58731 14.7453 9.68531 14.8685 9.7796 14.9611C10.0947 14.8858 10.4508 14.8055 10.766 14.7302C10.7652 14.4343 10.6646 14.138 10.4827 13.9043C10.1875 14.0936 9.82495 14.4203 9.53921 14.6222Z'
        fill='#3865BB'
      />
      <path
        d='M11.7836 16.1759C12.1634 17.4577 13.3539 18.4952 14.687 18.5716C15.7023 18.629 16.6735 18.1725 17.5478 17.6529C18.422 17.1333 19.2697 16.5316 20.2461 16.252C20.7607 16.1036 21.9847 16.1171 22.3216 15.6327C22.6637 15.1393 22.298 14.3613 21.9624 13.853C21.9039 13.7645 21.8477 13.6844 21.7955 13.6138C21.2395 12.8806 20.5831 12.2181 19.8236 11.7011C18.7473 10.9653 17.4504 10.5 16.1353 10.6484C15.9947 10.6649 15.8521 10.685 15.7117 10.7135C14.5354 10.9349 13.4173 11.5138 12.647 12.4269C11.7834 13.4482 11.4027 14.8959 11.7825 16.1777L11.7836 16.1759Z'
        fill='#3865BB'
      />
      <path
        d='M11.7829 16.176C12.1628 17.4578 13.3532 18.4953 14.6863 18.5717C15.7016 18.629 16.6729 18.1726 17.5471 17.653C18.4213 17.1334 19.2691 16.5317 20.2454 16.2521C20.76 16.1037 21.9841 16.1172 22.321 15.6328C22.663 15.1394 22.2973 14.3613 21.9617 13.8531C20.8457 13.933 19.3982 14.7315 18.7375 15.1743C17.5624 15.9633 16.3055 16.8361 14.8901 16.7907C14.1971 16.7694 13.4728 16.4846 13.0945 15.9017C12.6501 15.2187 12.7805 14.3078 13.0927 13.5566C13.5738 12.3931 14.4606 11.4019 15.563 10.7925C15.6117 10.7658 15.6605 10.7392 15.7082 10.7143C14.532 10.9357 13.4138 11.5146 12.6435 12.4278C11.7799 13.449 11.3992 14.8967 11.779 16.1785L11.7829 16.176Z'
        fill='#4488E1'
      />
      <path
        d='M15.2769 13.9217C15.8267 14.5417 15.7991 16.1909 14.9186 15.673C14.5204 15.4383 14.4312 14.9199 14.3535 14.5058C14.2654 14.0309 14.5404 13.4845 15.0687 13.7539C15.1459 13.7936 15.2155 13.8505 15.2779 13.9199L15.2769 13.9217Z'
        fill='#4CC9F0'
      />
      <path
        d='M19.8962 12.4533C20.3787 12.7319 20.712 13.789 20.041 13.647C19.737 13.5834 19.5699 13.2702 19.433 13.0244C19.2753 12.7404 19.3357 12.3346 19.7288 12.3924C19.7856 12.4014 19.8426 12.4224 19.898 12.4543L19.8962 12.4533Z'
        fill='#4CC9F0'
      />
    </g>
    <defs>
      <clipPath id='bo-ai-avatar-clip'>
        <path
          d='M0 16C0 7.16344 7.16344 0 16 0C24.8366 0 32 7.16344 32 16C32 24.8366 24.8366 32 16 32C7.16344 32 0 24.8366 0 16Z'
          fill='white'
        />
      </clipPath>
    </defs>
  </svg>
);

const BeneficialOwnershipWorkflow: React.FC = () => {
  const history = useHistory();
  const location = useLocation<{ resumeBeneficialOwnershipTicket?: any }>();
  const { cur_user, ticketDraft, setTicketDraft, resetTicketDraft } =
    useContext(GlobalContext);
  const logAppAction = useLogAppAction();
  const { enqueueSnackbar } = useSnackbar();
  const composerInputRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const workflowStateRef = useRef<Record<string, unknown>>({});
  // Latest bo_id seen on the wire, so an auto-reconnect resumes the SAME server-side
  // workflow instead of minting a fresh one (boId state lags a stream that drops).
  const boIdRef = useRef('');
  // Guards the completion credit spend against double-charging the same
  // investigation (e.g. a stream reconnect replaying the 'final' event) --
  // keyed by bo_id so a genuinely new investigation can still spend.
  const creditsSpentForBoIdRef = useRef<Set<string>>(new Set());
  // Agents whose `_completed` app action has already been logged for this
  // investigation, so replayed/resumed frames and re-sent statuses don't re-log.
  const loggedSectionsRef = useRef<Set<string>>(new Set());
  const isStreamingRef = useRef(false);
  const lastProgressKeyRef = useRef('');
  // Set when the backend asks the user to disambiguate the subject (a followup frame
  // carrying data.disambiguation). The turn that answers it runs with discovery already
  // done, so the backend emits no step_started (no Thinking chip). We open one client-
  // side instead — see answerFollowup — so the investigation-plan summary doesn't snap
  // in with no "thinking" feedback. Using a client chip (not a backend step_started)
  // avoids flipping the Discovery sidebar step back to "running".
  const disambiguationActiveRef = useRef(false);
  // Guards the resume-hydration effect so a saved ticket only rehydrates once per
  // mount, even though `location.state` stays referentially stable across re-renders.
  const resumeHydratedRef = useRef(false);
  // In step mode, agent completions are buffered here and rendered inside the next
  // pause bubble (so results + Continue buttons share one box). A ref (not state) so
  // consecutive stream frames see the current value synchronously.
  const pendingResultsRef = useRef<{ summary: string; findings: Finding[] }[]>(
    [],
  );
  const allowNavigationRef = useRef(false);
  const leaveSaveInProgressRef = useRef(false);
  // Mirrors `messages`, but updated synchronously inside addMessage — saveWork
  // reads this instead of the `messages` state variable so a save triggered in
  // the same event handler that just added a message (e.g. typing "2" to save
  // at a phase pause) doesn't race React's batched state update and persist a
  // transcript that's missing that very last bubble.
  const messagesRef = useRef<BOMessage[]>([]);
  // How many messages existed at the last successful save (or right after
  // resuming a saved ticket, when the transcript IS the save). Comparing this
  // to the live message count is how we tell "saved before" apart from "saved
  // AND still up to date" — see hasSavedCurrentState below.
  const lastSavedMessageCountRef = useRef(0);

  // Start empty so the assistant card shows the centered empty-state composer
  // (matching the Export workflow); the first user message switches to the thread.
  const [messages, setMessages] = useState<BOMessage[]>([]);
  // Block starting an investigation the team can't cover (charged on
  // completion). Re-read the balance whenever the workflow is back at its start.
  const {
    insufficient: insufficientCredits,
    remaining: remainingCredits,
    refresh: refreshCredits,
    consumeTrialRun: consumeTrialRun,
  } = useWorkflowCredits(BENEFICIAL_OWNERSHIP_CREDIT_COST, 'beneficial_ownership');
  const startBlockedByCredits = insufficientCredits && messages.length === 0;
  // Business+ (or super admin) is required to run the workflow; lower tiers get
  // the view-only RequiredLicense banner and every entry point is blocked.
  const userHasAccess =
    ['business', 'enterprise'].includes(cur_user?.license) ||
    cur_user?.bite_super_admin === true;
  useEffect(() => {
    if (messages.length === 0) {
      refreshCredits();
    }
  }, [messages.length, refreshCredits]);
  // Set when the team's one trial run of Beneficial Ownership has already
  // been spent (denial arrives as an HTTP 402 with a nonRetryable error, see
  // streamWorkflow's runOnce). Replaces the chat/composer with
  // TrialRunUsedPanel -- see the "Gate AI workflows..." ticket.
  const [trialLimitReached, setTrialLimitReached] = useState(false);
  const workflowGridRef = useScrollWorkflowIntoView(messages.length > 0);
  const [input, setInput] = useState('');
  const [entityName, setEntityName] = useState('');
  const [country, setCountry] = useState('');
  const [secondLayer, setSecondLayer] = useState(false);
  const [intakeStep, setIntakeStep] = useState<IntakeStep>('entity');
  const [isStreaming, setIsStreaming] = useState(false);
  const chatBodyRef = useWorkflowChatScroll(messages.length, isStreaming);
  const [boId, setBoId] = useState('');
  const [workflowState, setWorkflowState] = useState<Record<string, unknown>>(
    {},
  );
  const [followupItems, setFollowupItems] = useState<BOFollowupItem[]>([]);
  const [agentStatuses, setAgentStatuses] = useState<AgentStatus[]>(AGENTS);
  // Per-agent retained activity (search/record queries), keyed by agent key.
  // Per-agent result records (the actual matches/entities found), keyed by agent key.
  const [agentFindings, setAgentFindings] = useState<Record<string, Finding[]>>(
    {},
  );
  // Per-agent one-line result note (e.g. "No sanctions concerns found"), so the
  // sidebar card still has a dropdown even when an agent returned no records.
  const [agentResultText, setAgentResultText] = useState<
    Record<string, string>
  >({});
  // The completed final-analysis report, mirrored into the Final Analysis sidebar card.
  const [agentReport, setAgentReport] = useState<any>(null);
  // Which agent cards are expanded in the sidebar. The active agent auto-opens; the
  // user can toggle any card open/closed.
  const [, setExpandedAgents] = useState<Record<string, boolean>>({});
  // The control payload of the current phase pause (null when not awaiting a
  // decision). Its object identity also tags which pause message is actionable.
  const [pendingDecision, setPendingDecision] = useState<BOControl | null>(
    null,
  );
  // Entities the user has checked at a layer offer (ownership → second-layer targets;
  // second-layer → third-layer targets). Defaults to all discovered.
  const [selectedEntities, setSelectedEntities] = useState<string[]>([]);
  // Per-pause-message answer to a deeper-layer offer: "yes" reveals the tick list.
  // Client-only UI gate (keyed by control-message id) — it dispatches no command, so
  // it cannot hang the workflow; the Run action it leads to is the normal command.
  const [layerOfferYes, setLayerOfferYes] = useState<Record<number, boolean>>(
    {},
  );
  // Which option the user picked on a jurisdiction-mismatch confirm (keyed by message
  // id), so its buttons go inert once answered — mirrors layerOfferYes.
  const [confirmChosen, setConfirmChosen] = useState<Record<number, string>>(
    {},
  );
  const [stepsStatus, setStepsStatus] = useState<Record<string, BOStepStatus>>(
    {},
  );
  const [currentStep, setCurrentStep] = useState('');
  const [activeAgentKey, setActiveAgentKey] = useState('');
  const [finalData, setFinalData] = useState<any>(null);
  // How many of the final report's text segments (main description, sanctions
  // summary, each finding) have finished revealing. Gates them to stream in one
  // at a time instead of all typing in parallel — see renderReportBody.
  const [streamStage, setStreamStage] = useState(0);
  const [ticketId, setTicketId] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
  // Confirmation popup shown after a background "run & email me" run is handed off.
  const [notifyModalOpen, setNotifyModalOpen] = useState(false);
  const [pendingLocation, setPendingLocation] = useState<any>(null);
  const [disableLeaveWarning, setDisableLeaveWarning] = useState(
    () => localStorage.getItem(leaveWarningStorageKey) === 'true',
  );
  const [newTicketName, setNewTicketName] = useState<string>('');
  const [shareEmail, setShareEmail] = useState<string[]>([]);
  // Re-stamped once a second while streaming, and read directly by the live
  // "Thinking for Ns" counter (see renderThinkingTranscript) — so the number shown is
  // always taken from the same clock reading that triggered the render.
  const [nowMs, setNowMs] = useState(() => Date.now());

  const formattedDate = useMemo(() => {
    const dt = new Date();
    const dtWithoutTz = new Date(
      dt.valueOf() + dt.getTimezoneOffset() * 60 * 1000,
    );
    return format(dtWithoutTz, 'yyyy-MM-dd');
  }, []);

  // Id of the most recent status line — only that one (the current step) shows the
  // animated "..." dots; earlier ones freeze to a static ellipsis once done.
  const lastStatusMessageId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].type === 'status') {
        return messages[i].id;
      }
    }
    return null;
  }, [messages]);

  const savedTicketId = ticketDraft?._id || ticketId;
  // `savedTicketId` only means "this investigation was saved at some point" — it
  // stays truthy forever once a save succeeds, even after the conversation moves
  // on with new unsaved messages. Gate the "already saved" UI (View saved instead
  // of Save, skip-the-modal-and-navigate-away) on THIS instead, so it only reads
  // saved when nothing has happened since that save.
  const hasSavedCurrentState =
    Boolean(savedTicketId) &&
    messages.length <= lastSavedMessageCountRef.current;
  const hasUnsavedWorkflowWork = Boolean(
    !hasSavedCurrentState &&
      (entityName ||
        finalData ||
        boId ||
        followupItems.length > 0 ||
        messages.some((message) => message.sender === 'user')),
  );
  const shouldWarnOnLeave =
    !disableLeaveWarning && hasUnsavedWorkflowWork && !isModalOpen;

  // Advance the live elapsed-time counter while a stream is running (see
  // renderThinkingTranscript).
  useEffect(() => {
    if (!isStreaming) {
      return undefined;
    }
    // Stamp immediately as well as on the interval: the interval only runs while
    // streaming, so without this the first second of a new turn would measure against
    // whenever the PREVIOUS turn last ticked.
    setNowMs(Date.now());
    // 1s so the counter ticks one second at a time instead of jumping in
    // whatever-ms chunks.
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [isStreaming]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (isStreaming || isModalOpen) {
      return undefined;
    }

    const focusTimer = window.setTimeout(() => {
      composerInputRef.current?.focus();
    }, 0);

    return () => window.clearTimeout(focusTimer);
  }, [isStreaming, isModalOpen]);

  // Auto-expand the agent that just became active so its live activity is visible,
  // without ever auto-collapsing a card the user opened.
  useEffect(() => {
    if (!activeAgentKey) {
      return;
    }
    setExpandedAgents((prev) =>
      prev[activeAgentKey] ? prev : { ...prev, [activeAgentKey]: true },
    );
  }, [activeAgentKey]);

  // Catch-all sync for the mutators that update `messages` without going
  // through addMessage (addProgressMessage, thinking-chip helpers, the stream
  // event handler) — addMessage keeps messagesRef current synchronously for
  // its own race; this keeps it current for everything else.
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    if (!shouldWarnOnLeave) {
      return undefined;
    }

    const beforeUnloadHandler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', beforeUnloadHandler);
    return () => {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
    };
  }, [shouldWarnOnLeave]);

  const navigateToPendingLocation = useCallback(() => {
    if (!pendingLocation) {
      return;
    }

    history.push({
      pathname: pendingLocation.pathname,
      search: pendingLocation.search,
      hash: pendingLocation.hash,
      state: pendingLocation.state,
    });
  }, [history, pendingLocation]);

  const continueToPendingLocation = useCallback(() => {
    allowNavigationRef.current = true;
    navigateToPendingLocation();
    window.setTimeout(() => {
      allowNavigationRef.current = false;
      setPendingLocation(null);
    }, 0);
  }, [navigateToPendingLocation]);

  const cancelPendingLeaveSave = () => {
    if (!leaveSaveInProgressRef.current) {
      return;
    }

    leaveSaveInProgressRef.current = false;
    setPendingLocation(null);
  };

  const handleBlockedNavigation = (nextLocation: any) => {
    if (allowNavigationRef.current) {
      return true;
    }

    if (!shouldWarnOnLeave) {
      return true;
    }

    setPendingLocation(nextLocation);
    setIsLeaveModalOpen(true);
    return false;
  };

  const addMessage = useCallback((message: Omit<BOMessage, 'id'>) => {
    setMessages((prev) => {
      const next = [
        ...prev,
        {
          ...message,
          id: Date.now() + Math.floor(Math.random() * 1000),
        },
      ];
      messagesRef.current = next;
      return next;
    });
  }, []);

  const removeThinkingMessages = useCallback(() => {
    setMessages((prev) =>
      prev.filter((message) => !isThinkingMessage(message)),
    );
  }, []);

  // Charges on successful completion (the backend's 'final' SSE event), not
  // on start -- an interrupted/errored run never reaches this. Fire-and-forget:
  // a logging failure here shouldn't block the user from seeing their
  // completed investigation.
  const spendWorkflowCompletionCredit = useCallback(
    async (boIdForRun: string) => {
      const teamId = cur_user?.selected_team?.id;
      if (!teamId) return;

      // The team's free trial run: nothing to charge.
      if (consumeTrialRun()) return;

      try {
        // The unified spend chooses trial only when it can cover the complete
        // charge, then falls back to paid credits without splitting one run.
        const response = await CreditsAPI.spendCredits({
          teamId: teamId,
          source: 'beneficial_ownership',
          referenceId: boIdForRun || null,
          userId: cur_user?.basics?.id || null,
          userName: [cur_user?.basics?.firstName, cur_user?.basics?.lastName].filter(Boolean).join(' ') || null,
          userEmail: cur_user?.basics?.email || null,
        });

        if (!response?.success) {
          console.error('[BO] Failed to log credit spend', response);
        }


      } catch (error) {
        // Never surface this to the user -- the investigation itself already
        // completed successfully.
        console.error('[BO] Failed to log credit spend', error);
      }
    },
    [cur_user?.selected_team?.id, consumeTrialRun],
  );

  const showThinkingMessage = useCallback(() => {
    setMessages((prev) => [
      ...prev.filter((message) => !isThinkingMessage(message)),
      {
        id: Date.now() + Math.floor(Math.random() * 1000),
        sender: 'bot',
        type: 'question',
        text: 'Thinking...',
      },
    ]);
  }, []);

  const addProgressMessage = useCallback((message: Omit<BOMessage, 'id'>) => {
    setMessages((prev) => [
      // Drop any stray plain "Thinking..." bubble; the ChatKit chip is the only
      // thinking indicator now (this used to re-append one after each progress line,
      // which produced the "double thinking" below the card).
      ...prev.filter((item) => !isThinkingMessage(item)),
      {
        ...message,
        id: Date.now() + Math.floor(Math.random() * 1000),
      },
    ]);
  }, []);

  // --- Thinking transcript helpers -------------------------------------------
  // Each pipeline phase gets ONE "Thinking" transcript. step_started opens it;
  // tool_activity(search), heartbeat and token_delta(kind='thinking') APPEND into the
  // currently-running one; step_completed / final finalize it (stamping the elapsed
  // time so it settles as "Thought for Ns"). Phases are strictly sequential, so "the
  // active transcript" is the last message with a running thinking.

  // Mutate the currently-running thinking transcript in place (no-op if none is open).
  //
  // An updater that declines the change returns its argument unchanged, and this then
  // returns `prev` so React bails out entirely. Several callers hit that path routinely
  // — a duplicate line in appendThinkingStep, or a heartbeat once the phase's bounded
  // sequence is spent — and the analysis phase now keepalives every few seconds for
  // minutes at a time, so without the identity check each of those no-ops would still
  // allocate a new array + message and re-render the whole conversation.
  const updateRunningThinking = useCallback(
    (updater: (thinking: BOThinking) => BOThinking) => {
      setMessages((prev) => {
        for (let i = prev.length - 1; i >= 0; i -= 1) {
          const message = prev[i];
          if (message.type === 'thinking' && message.thinking?.running) {
            const updated = updater(message.thinking);
            if (updated === message.thinking) {
              return prev;
            }
            const next = [...prev];
            next[i] = { ...message, thinking: updated };
            return next;
          }
        }
        return prev;
      });
    },
    [],
  );

  // Mark the running transcript's phase as genuinely finished, so it earns a "Done"
  // row when it freezes. Separate from finalizeRunningThinking because the two do not
  // always coincide: an agent phase completes and freezes together, while discovery
  // completes on one turn but only freezes when that turn's stream ends.
  const markRunningThinkingCompleted = useCallback(() => {
    updateRunningThinking((thinking) => ({ ...thinking, completed: true }));
  }, [updateRunningThinking]);

  // Freeze any running chip: stop the animation and stamp its elapsed time.
  const finalizeRunningThinking = useCallback(() => {
    setMessages((prev) =>
      prev.map((message) =>
        message.type === 'thinking' && message.thinking?.running
          ? {
              ...message,
              thinking: {
                ...message.thinking,
                running: false,
                elapsedMs: Date.now() - message.thinking.startedAt,
              },
            }
          : message,
      ),
    );
  }, []);

  // Open a fresh chip for a phase, finalizing any straggler and clearing the plain
  // "Thinking..." bubble the chip now supersedes.
  const startThinkingTranscript = useCallback((agentKey: string, header: string) => {
    setMessages((prev) => {
      const cleaned = prev
        .filter((message) => !isThinkingMessage(message))
        .map((message) =>
          message.type === 'thinking' && message.thinking?.running
            ? {
                ...message,
                thinking: {
                  ...message.thinking,
                  running: false,
                  elapsedMs: Date.now() - message.thinking.startedAt,
                },
              }
            : message,
        );

      return [
        ...cleaned,
        {
          id: Date.now() + Math.floor(Math.random() * 1000),
          sender: 'bot',
          type: 'thinking',
          text: '',
          thinking: {
            agentKey: agentKey,
            header: header || '',
            steps: [],
            thinkingText: '',
            heartbeats: 0,
            running: true,
            completed: false,
            startedAt: Date.now(),
            elapsedMs: 0,
          },
        } as BOMessage,
      ];
    });
  }, []);

  // Append one line to the running transcript. Every line stays visible for the rest
  // of the phase — this is what makes the block read as progress without any
  // timer-driven trickery.
  const appendThinkingStep = useCallback(
    (text: string, level: BOThoughtLevel = 'step') => {
      const trimmed = (text || '').trim();
      if (!trimmed) {
        return;
      }
      updateRunningThinking((thinking) => {
        // Skip consecutive duplicates so a repeated query doesn't stack. Only the LAST
        // line is compared (not the whole list): the same source kind legitimately
        // recurs later in a phase, and suppressing that would hide real activity.
        const last = thinking.steps[thinking.steps.length - 1];
        if (last?.text === trimmed) {
          return thinking;
        }
        return {
          ...thinking,
          steps: [...thinking.steps, { text: trimmed, level: level }],
        };
      });
    },
    [updateRunningThinking],
  );

  // A backend keepalive fired: the running phase has been silent for ~10s. Append the
  // next line of its heartbeat sequence, then stop once the sequence is spent (so a
  // two-minute silence never turns into twelve identical lines).
  const appendThinkingHeartbeat = useCallback(() => {
    updateRunningThinking((thinking) => {
      const lines =
        HEARTBEAT_LINES[thinking.agentKey] || HEARTBEAT_LINES_DEFAULT;
      if (thinking.heartbeats >= lines.length) {
        return thinking;
      }
      return {
        ...thinking,
        heartbeats: thinking.heartbeats + 1,
        steps: [
          ...thinking.steps,
          { text: lines[thinking.heartbeats], level: 'step' as BOThoughtLevel },
        ],
      };
    });
  }, [updateRunningThinking]);

  const appendThinkingText = useCallback(
    (text: string) => {
      updateRunningThinking((thinking) => ({
        ...thinking,
        thinkingText: thinking.thinkingText + text,
      }));
    },
    [updateRunningThinking],
  );

  const updateAgentStatus = useCallback(
    (agentKey: string, status: AgentStatus['status']) => {
      if (!agentKey) {
        return;
      }

      setAgentStatuses((prev) =>
        prev.map((agent) =>
          agent.key === agentKey
            ? { ...agent, status: strongerStatus(agent.status, status) }
            : agent,
        ),
      );
    },
    [],
  );

  const updateAgentStatusesFromSteps = useCallback(
    (incomingStepsStatus?: Record<string, BOStepStatus>) => {
      if (!incomingStepsStatus) {
        return;
      }

      setAgentStatuses((prev) => {
        const next = prev.map((agent) => ({ ...agent }));
        Object.entries(incomingStepsStatus).forEach(([stepKey, stepStatus]) => {
          const agentKey = STEP_TO_AGENT[stepKey];
          const agent = next.find((item) => item.key === agentKey);
          if (agent) {
            agent.status = strongerStatus(
              agent.status,
              statusFromStep(stepStatus),
            );
          }
        });
        return next;
      });
    },
    [],
  );

  const createRequestPayload = (
    overrides: Partial<Record<string, unknown>> = {},
  ) => ({
    bo_id: boId || undefined,
    entity_name: entityName,
    country: country || undefined,
    second_layer: secondLayer,
    auto_run: true,
    followup_items: followupItems,
    workflow_state: workflowStateRef.current || workflowState || {},
    group_id: cur_user.selected_team?.id,
    ...overrides,
  });

  const handleWorkflowEvent = useCallback(
    (event: BOStreamEvent) => {
      // token_delta frames stream the final-analysis generation. We surface the
      // model's *thinking* (kind='thinking') live inside that phase's Thinking chip,
      // but ignore the report *text* (kind='text') — it's raw JSON, and the parsed
      // result renders as the final summary once complete.
      if (event.event === 'token_delta') {
        const kind = event.data?.kind;
        const text = event.data?.text;
        if (kind === 'thinking' && text) {
          appendThinkingText(text);
        }
        return;
      }

      // Keepalive frame from a long, wire-silent phase (see backend build 4). It
      // carries no state, but it IS the only honest signal that a silent phase is
      // still alive, so it appends the next line of that phase's bounded heartbeat
      // sequence into the running transcript.
      if (event.event === 'heartbeat') {
        appendThinkingHeartbeat();
        return;
      }

      if (event.bo_id) {
        boIdRef.current = event.bo_id;
        setBoId(event.bo_id);
      }

      if (event.workflow_state) {
        workflowStateRef.current = event.workflow_state;
        setWorkflowState(event.workflow_state);
      }

      if (event.steps_status) {
        // A section only counts when it actually ran: statusFromStep reports
        // 'skipped' separately, so a skipped layer never logs a completion.
        Object.entries(event.steps_status).forEach(([stepKey, stepStatus]) => {
          const agentKey = STEP_TO_AGENT[stepKey];
          if (
            SECTION_LOG_AGENTS.has(agentKey) &&
            statusFromStep(stepStatus) === 'done' &&
            !loggedSectionsRef.current.has(agentKey)
          ) {
            loggedSectionsRef.current.add(agentKey);
            logAppAction(`beneficial_ownership_workflow:${agentKey}_completed`);
          }
        });
        setStepsStatus((prev) => ({ ...prev, ...event.steps_status }));
        updateAgentStatusesFromSteps(event.steps_status);
      }

      if (event.current_step) {
        setCurrentStep(event.current_step);
        setActiveAgentKey(STEP_TO_AGENT[event.current_step] || '');
      }

      if (event.stage) {
        const agentKey = STAGE_TO_AGENT[event.stage];
        setActiveAgentKey(agentKey || '');
        if (event.event === 'phase_started' || event.event === 'step_started') {
          updateAgentStatus(agentKey, 'running');
        }
        if (
          event.event === 'phase_completed' ||
          event.event === 'step_completed'
        ) {
          updateAgentStatus(agentKey, 'done');
        }
        if (event.event === 'workflow_error') {
          updateAgentStatus(agentKey, 'error');
        }
      }

      // A phase completed → close its Thinking transcript with the DETERMINATION it
      // reached, then freeze it to "Thought for Ns". Appending the summary here is what
      // makes each settled block end on what the phase FOUND rather than on the last
      // thing it happened to be doing. (Result buffering / sidebar findings are handled
      // in the dedicated step_completed block further below.)
      // The 'followup' stage is excluded: its step_completed carries a chat answer to
      // a user question, not a phase determination, and it is rendered as a normal bot
      // reply further below.
      if (event.event === 'step_completed') {
        if (event.stage !== 'followup') {
          const summary = phaseSummaryNote(event.message);
          if (summary) {
            appendThinkingStep(summary, 'finding');
          }
        }
        markRunningThinkingCompleted();
        finalizeRunningThinking();
      }

      // phase_completed is emitted from exactly ONE place in the backend: discovery,
      // the moment the intake PROFILE is filled in. That is an internal milestone, not
      // the end of discovery — the profile being complete is what lets it go on to
      // disambiguate the entity ("which of these four records did you mean?") and
      // confirm the jurisdiction, either of which asks the user another question.
      //
      // So it must do neither of the two things it looks like it should:
      //   * it must NOT mark the transcript completed — discovery finishes when the
      //     investigation plan lands (see the phase_paused handler below), and marking
      //     it here put a "Done" above a question that was still waiting to be answered;
      //   * its message must NOT reach the chat — an italic "Discovery complete." sat
      //     directly above "Which one did you mean?", flatly contradicting it.
      // Consumed here so it can't fall through to the generic status renderer. The
      // state it carries (workflow_state / steps_status) was already applied above, and
      // nothing reads its `data.discovery` payload.
      if (event.event === 'phase_completed') {
        return;
      }

      // Live tool activity from the running agent — a query issued, a source reviewed,
      // a sub-stage heading, or a determination. Appends into the running phase's
      // Thinking transcript at the level the backend tagged it with (data.level; see
      // tel.chip_level). An untagged frame is a plain step, so older/replayed frames
      // still render correctly.
      if (event.event === 'tool_activity') {
        const activity = event.data || {};
        if (activity.kind === 'search' && activity.detail) {
          const level: BOThoughtLevel =
            activity.level === 'group' ||
            activity.level === 'finding' ||
            activity.level === 'phase'
              ? activity.level
              : 'step';
          appendThinkingStep(String(activity.detail), level);
        }
        return;
      }

      // A phase begins → open its Thinking chip (the step_started message becomes the
      // chip's step line). Returning here keeps the old loose italic header line out
      // of the generic message renderer below.
      if (event.event === 'step_started') {
        const agentKey =
          (event.stage && STAGE_TO_AGENT[event.stage]) ||
          (event.current_step && STEP_TO_AGENT[event.current_step]) ||
          '';
        startThinkingTranscript(agentKey, event.message);
        return;
      }

      if (event.followup_items?.length) {
        setFollowupItems(event.followup_items);
      }

      if (event.event === 'followup') {
        // A disambiguation pick ("Which one did you mean?") or a jurisdiction-mismatch
        // confirm ("...records show X in United States. Is this the entity?") — both
        // are answered on a discovery-already-done turn with no backend step_started,
        // so remember it and open a client-side Thinking chip (see answerFollowup).
        if (event.data?.disambiguation || event.data?.jurisdiction_confirm) {
          disambiguationActiveRef.current = true;
        }
        // The jurisdiction-mismatch confirm renders as numbered option buttons (like the
        // other decision gates), not plain question text — hand it its own message type
        // carrying the option labels.
        if (event.data?.jurisdiction_confirm) {
          removeThinkingMessages();
          addMessage({
            sender: 'bot',
            type: 'jurisdiction_confirm',
            stage: event.stage,
            text: event.message,
            data: event.data.jurisdiction_confirm,
          });
          return;
        }
        removeThinkingMessages();
        // Only show questions that still need an answer. The backend echoes the
        // full followup_items list (including already-answered ones), so mapping
        // over all of them would re-display prior questions.
        const pendingQuestions =
          event.followup_items
            ?.filter((item) => !item.answer)
            .map((item) => item.question)
            .join('\n') || '';
        addMessage({
          sender: 'bot',
          type: 'question',
          stage: event.stage,
          text: pendingQuestions || event.message,
        });
        return;
      }

      if (event.event === 'phase_paused') {
        removeThinkingMessages();
        // A pause carrying the intake summary IS discovery's finish line — the
        // "Here's what I have for this investigation" plan. This is the ONLY thing that
        // completes discovery: phase_completed fires earlier, while the entity may still
        // need disambiguating (see its handler above). Finalize here as well as mark, so
        // the "Done" row lands in the same commit as the plan bubble instead of waiting
        // on the stream teardown to freeze it.
        if (event.data?.discovery_summary) {
          markRunningThinkingCompleted();
          finalizeRunningThinking();
        }
        const control = event.control || {};
        // Same object reference is stored on the message and as pendingDecision, so
        // only this (latest) pause's buttons are actionable.
        setPendingDecision(control);
        // Fold the phase's buffered agent results into this pause bubble, so results
        // and the Continue buttons live in the same box.
        const results = pendingResultsRef.current;
        pendingResultsRef.current = [];
        // The ownership pause offers a second-layer investigation; the second-layer
        // pause offers a third-layer one. The tick list starts EMPTY: pre-seeding every
        // discovered entity made "all" the default, which is why runs went wide (layer
        // wall-clock measured 48s at 3 ticked, 67s at 5, 87s at 8). Breadth is opted
        // into now, and "Select all" in the picklist is one click.
        if (
          control.active_phase === 'ownership' ||
          control.active_phase === 'second_layer'
        ) {
          setSelectedEntities([]);
        }
        addMessage({
          sender: 'bot',
          type: 'control',
          stage: event.stage,
          text: event.message,
          data: {
            control: control,
            results: results,
            // Structured intake summary (entity / country / focus) for the discovery
            // gate — rendered as an aligned list rather than pre-formatted text.
            discovery_summary: event.data?.discovery_summary,
          },
        });
        return;
      }

      if (event.event === 'final') {
        removeThinkingMessages();
        markRunningThinkingCompleted();
        finalizeRunningThinking();
        setFinalData(event.data || {});
        setStreamStage(0);
        setAgentReport(event.data || {});
        updateAgentStatus('analysis', 'done');
        setActiveAgentKey('analysis');
        setCurrentStep('');
        addMessage({
          sender: 'bot',
          type: 'final',
          stage: 'final',
          text: '',
          data: event.data || {},
        });

        try {
          logAppAction('beneficial_ownership_workflow:completed');
        } catch (error) {
          console.error('[BO] Failed to log investigation completion', error);
        }

        const completedBoId = boIdRef.current;
        if (
          completedBoId &&
          !creditsSpentForBoIdRef.current.has(completedBoId)
        ) {
          creditsSpentForBoIdRef.current.add(completedBoId);
          spendWorkflowCompletionCredit(completedBoId);
        }
        return;
      }

      if (event.event === 'notify_scheduled') {
        // The user chose "run & email me": the investigation now runs server-side and
        // the saved-work ticket will be updated + an email sent on completion. Nothing
        // more streams on this connection, so drop the Thinking chips, suppress the
        // unsaved-work leave warning (the run is safely handed off + already saved), and
        // show a single confirmation popup. Acknowledging it resets to a fresh workflow.
        removeThinkingMessages();
        finalizeRunningThinking();
        setDisableLeaveWarning(true);
        setActiveAgentKey('');
        setCurrentStep('');
        setNotifyModalOpen(true);
        return;
      }

      if (event.event === 'workflow_error') {
        // Log the real backend error to the console for debugging, but show the user a
        // friendly, non-technical message (never the raw error text). See build 13.
        // eslint-disable-next-line no-console
        console.error(
          '[BO] workflow_error',
          event.data?.error || event.message,
        );
        removeThinkingMessages();
        finalizeRunningThinking();
        const isBusy = event.data?.error_code === 'service_busy';
        addMessage({
          sender: 'bot',
          type: 'error',
          stage: event.stage,
          text: isBusy ? SERVICE_BUSY_MESSAGE : WORKFLOW_ERROR_MESSAGE,
        });
        return;
      }

      // A follow-up answer about the completed investigation comes back as a
      // step_completed on the 'followup' stage. It is NOT an agent-phase result (no
      // findings, no sidebar step), so the agent-completion handler below would buffer
      // it for a pause bubble that never comes (step mode) or drop it for having no
      // findings (auto mode) — either way the answer vanishes. Render it as a normal
      // bot reply instead.
      if (event.event === 'step_completed' && event.stage === 'followup') {
        finalizeRunningThinking();
        removeThinkingMessages();
        if (event.message) {
          addMessage({
            sender: 'bot',
            type: 'answer',
            stage: 'followup',
            text: event.message,
          });
        }
        return;
      }

      // Agent completion. Retain its records for the sidebar. In step mode, buffer
      // the result so it renders INSIDE the upcoming pause bubble (results + Continue
      // buttons in one box). In auto mode (run-all, no pauses) render a result card
      // inline; a completion with no records falls through to the plain status line.
      if (event.event === 'step_completed') {
        const findings = (
          Array.isArray(event.data?.findings) ? event.data.findings : []
        ) as Finding[];
        const doneAgentKey =
          STEP_TO_AGENT[event.current_step || ''] ||
          STAGE_TO_AGENT[event.stage || ''] ||
          '';
        if (doneAgentKey && findings.length > 0) {
          setAgentFindings((prev) => ({ ...prev, [doneAgentKey]: findings }));
        }
        // Store a one-line note (the part after "— ") so the sidebar card always has
        // dropdown content, even when the agent returned no records.
        if (doneAgentKey) {
          const note = phaseSummaryNote(event.message);
          if (note) {
            setAgentResultText((prev) => ({ ...prev, [doneAgentKey]: note }));
          }
        }
        if (event.control?.run_mode === 'step') {
          pendingResultsRef.current.push({
            summary: String(event.message || ''),
            findings: findings,
          });
          return;
        }
        if (findings.length > 0) {
          addProgressMessage({
            sender: 'bot',
            type: 'agent_result',
            stage: event.stage,
            text: event.message,
            data: { findings: findings },
          });
          return;
        }
        // auto mode + no records → the finalized Thinking chip ("Thought for Ns")
        // already conveys the phase completed, so don't add a loose status line.
        return;
      }

      // Fallback for any remaining event that still carries a message (phase
      // start/completion now flow into the Thinking chip above and return early).
      if (event.message) {
        const progressKey = `${event.event || ''}:${event.stage || ''}:${
          event.current_step || ''
        }:${event.message}`;

        if (progressKey === lastProgressKeyRef.current) {
          return;
        }

        lastProgressKeyRef.current = progressKey;
        addProgressMessage({
          sender: 'bot',
          type: 'status',
          stage: event.stage,
          text: event.message,
        });
      }
    },
    [
      addMessage,
      addProgressMessage,
      appendThinkingHeartbeat,
      appendThinkingStep,
      appendThinkingText,
      finalizeRunningThinking,
      markRunningThinkingCompleted,
      removeThinkingMessages,
      startThinkingTranscript,
      updateAgentStatus,
      updateAgentStatusesFromSteps,
    ],
  );

  const streamWorkflow = useCallback(
    async (initialBody: Record<string, unknown>) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      // A new stream supersedes any pending pause decision and its buffered results.
      setPendingDecision(null);
      pendingResultsRef.current = [];
      setIsStreaming(true);
      isStreamingRef.current = true;
      // No plain "Thinking..." placeholder — every phase (discovery included) opens the
      // ChatKit Thinking card on its step_started, so the placeholder only caused the
      // "double thinking" (card + loose Thinking line).

      // A long phase (esp. sanctions with all layers) can have the browser/OS suspend
      // the streaming connection mid-flight (net::ERR_NETWORK_IO_SUSPENDED). The backend
      // checkpoints per phase and we round-trip the full workflow_state, so a drop is
      // recoverable: resend the last command and the server replays settled phases
      // instantly and re-runs only the interrupted one. The heartbeat frames (backend
      // build 4) keep the connection warm to make this rare in the first place.
      const MAX_RECONNECTS = 4;
      let body = initialBody;
      let attempt = 0;
      // How far the workflow has actually got, as the number of steps the backend
      // reports settled (it echoes steps_status on every frame). "Made progress"
      // has to mean this number moved — NOT merely that a frame arrived. A
      // reconnect re-runs the interrupted phase and so always emits at least its
      // step_started, which would reset the reconnect budget on every attempt and
      // leave a phase that fails repeatedly retrying forever.
      let settledSteps = 0;
      // Declared once, outside the reconnect loop, so the promise executor only
      // closes over its own parameter (`ms`) — no-loop-func otherwise flags an
      // inline executor referencing the loop's mutable `attempt`.
      const sleep = (ms: number) =>
        new Promise((resolve) => {
          window.setTimeout(resolve, ms);
        });

      // One network attempt. Resolves on a clean end (server closed the stream after a
      // pause/final frame); throws on an unexpected drop so the caller can reconnect.
      const runOnce = async (): Promise<void> => {
        const response = await fetch(
          `${apiUrl}/beneficial-ownership/workflow`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/x-ndjson',
              Authorization: `Bearer ${KeycloakService.getToken()}`,
            },
            body: JSON.stringify(body),
            signal: controller.signal,
          },
        );

        if (!response.ok) {
          // A trial-limit denial (see the "Gate AI workflows..." ticket) is
          // permanent for this team/bucket -- retrying will 402 every time, so
          // it must bypass the reconnect loop below instead of burning all
          // MAX_RECONNECTS attempts against a guaranteed failure.
          if (response.status === 402) {
            let detail = '';
            try {
              detail = (await response.json())?.detail || '';
            } catch {
              // fall through with an empty detail
            }
            const err: any = new Error(
              detail || 'Your team’s trial for this workflow has been used.',
            );
            err.nonRetryable = true;
            throw err;
          }
          throw new Error(`HTTP ${response.status}: ${await response.text()}`);
        }
        if (!response.body) {
          throw new Error('No response body returned');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        // Per attempt, not per stream: whether the server closed on a frame that
        // legitimately ends a turn. A dropped fetch usually surfaces as done: true
        // rather than an error, so without this a truncated stream reads as a clean
        // finish and never reconnects.
        let sawTurnEnd = false;

        // Declared once, outside the read loop, so no-loop-func doesn't flag its
        // closure over `settledSteps` / `sawTurnEnd` (reassigned below, not just read).
        const handleLine = (line: string) => {
          const trimmed = line.trim();
          if (!trimmed) {
            return;
          }
          const parsed = JSON.parse(trimmed) as BOStreamEvent;
          // Heartbeats keep the socket warm but carry no state at all — they can
          // neither advance the progress mark nor end a turn.
          if (parsed.event !== 'heartbeat') {
            settledSteps = Math.max(settledSteps, countSettledSteps(parsed));
            if (parsed.event && TURN_END_EVENTS.has(parsed.event)) {
              sawTurnEnd = true;
            }
          }
          handleWorkflowEvent(parsed);
        };

        while (true) {
          const { value, done } = await reader.read();
          if (done) {
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          lines.forEach(handleLine);
        }

        const remaining = buffer.trim();
        if (remaining) {
          handleLine(remaining);
        }

        // EOF with no turn-ending frame means the connection was cut mid-phase,
        // not that the workflow finished. Throwing hands it to the reconnect loop,
        // which resumes from the last checkpoint instead of leaving the run looking
        // silently complete.
        if (!sawTurnEnd) {
          throw new Error('Workflow stream ended before a terminal event');
        }
      };

      try {
        // Reconnect loop: a clean end breaks out; an unexpected drop resends the last
        // command. The budget is bounded, but it RESETS whenever an attempt settled a
        // new step, so a long run that drops repeatedly still recovers as long as it
        // keeps advancing — a phase that keeps failing at the same point does not
        // refund anything and gives up after MAX_RECONNECTS.
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const settledStepsBefore = settledSteps;
          try {
            await runOnce();
            break;
          } catch (error: any) {
            // A newer stream superseded this one (user acted / navigated) — bail quietly.
            if (error?.name === 'AbortError' || controller.signal.aborted) {
              return;
            }
            // Permanent denial, not a dropped connection -- skip the retry
            // budget entirely and let the outer catch show the real reason.
            if (error?.nonRetryable) {
              throw error;
            }
            // Refund the budget only if this attempt settled a step the previous
            // ones hadn't. Resetting on "any frame arrived" gave the replay of the
            // interrupted phase credit for progress it never made.
            if (settledSteps > settledStepsBefore) {
              attempt = 0;
            }
            attempt += 1;
            if (attempt > MAX_RECONNECTS) {
              throw error;
            }
            removeThinkingMessages();
            addMessage({
              sender: 'bot',
              type: 'status',
              text: `Connection interrupted — reconnecting (${attempt}/${MAX_RECONNECTS})…`,
            });
            // Resume from the freshest state/id we've received so the replay skips every
            // phase already completed (works even if a different backend worker answers).
            body = {
              ...body,
              bo_id: boIdRef.current || body.bo_id,
              workflow_state: workflowStateRef.current || body.workflow_state,
            };
            await sleep(1000 * attempt);
            if (controller.signal.aborted) {
              return;
            }
          }
        }
      } catch (error: any) {
        if (error?.name !== 'AbortError' && !controller.signal.aborted) {
          removeThinkingMessages();
          if (error?.nonRetryable) {
            // Replaces the whole chat/composer area with TrialRunUsedPanel
            // instead of a chat bubble -- see the panel's render branch below.
            setTrialLimitReached(true);
          } else {
            // Keep the raw error in the console for debugging; show friendly copy only.
            // eslint-disable-next-line no-console
            console.error('[BO] workflow stream failed', error);
            addMessage({
              sender: 'bot',
              type: 'error',
              text: WORKFLOW_ERROR_MESSAGE,
            });
            enqueueSnackbar('Unable to run beneficial ownership workflow.', {
              variant: 'error',
            });
          }
        }
      } finally {
        isStreamingRef.current = false;
        removeThinkingMessages();
        // Never leave a chip spinning if the stream ends without a final/error frame
        // (aborted, network drop, etc.).
        finalizeRunningThinking();
        setIsStreaming(false);
      }
    },
    [
      addMessage,
      enqueueSnackbar,
      finalizeRunningThinking,
      handleWorkflowEvent,
      removeThinkingMessages,
      showThinkingMessage,
    ],
  );

  // Answer a phase pause with a structured workflow-control command. `echoLabel`
  // adds a user chat bubble for a button click; typed replies (already echoed by
  // handleSubmit) omit it.
  const sendCommand = (
    action: 'next' | 'run_all',
    echoLabel?: string,
    selectedEntitiesArg?: string[],
  ) => {
    if (isStreaming || !userHasAccess) {
      return;
    }
    if (echoLabel) {
      addMessage({ sender: 'user', text: echoLabel });
    }
    const command: {
      action: string;
      selected_entities?: string[];
    } = { action: action };
    if (selectedEntitiesArg !== undefined) {
      command.selected_entities = selectedEntitiesArg;
    }
    streamWorkflow(createRequestPayload({ command: command }));
  };

  const toggleEntitySelection = (title: string) => {
    setSelectedEntities((prev) =>
      prev.includes(title)
        ? prev.filter((entry) => entry !== title)
        : [...prev, title],
    );
  };

  // Picking "Yes" on a layer offer appends a SEPARATE picklist bubble below (the
  // workflow's response to the choice), carrying the same control + results. It sends
  // no command (client-only), so it can't hang; the Run inside it dispatches normally.
  const revealLayerPicklist = (message: BOMessage) => {
    if (layerOfferYes[message.id]) {
      return;
    }
    setLayerOfferYes((prev) => ({ ...prev, [message.id]: true }));
    const data = (message.data || {}) as {
      control?: BOControl;
      results?: { summary: string; findings: Finding[] }[];
    };
    addMessage({
      sender: 'bot',
      type: 'layer_picklist',
      text: '',
      data: { control: data.control, results: data.results || [] },
    });
  };

  const startInvestigation = (
    payloadOverrides = {},
    // _displayName retained for call-site compatibility; the discovery Thinking card
    // now announces the subject, so we no longer emit a loose "Starting
    // investigation: X" status line before it.
    _displayName = entityName,
  ) => {
    setIntakeStep('ready');
    setActiveAgentKey('discovery');
    loggedSectionsRef.current = new Set();
    logAppAction('beneficial_ownership_workflow:started');
    streamWorkflow(createRequestPayload(payloadOverrides));
  };

  const answerFollowup = (answer: string) => {
    const unanswered = followupItems.filter((item) => !item.answer);
    const updatedFollowups = followupItems.map((item) =>
      !item.answer && item.question === unanswered[0]?.question
        ? { ...item, answer: answer }
        : item,
    );

    setFollowupItems(updatedFollowups);
    // Answering a disambiguation pick or a jurisdiction-mismatch confirm resolves it
    // and jumps straight to the investigation-plan summary (a phase_paused frame).
    // Discovery is already done, so the backend emits no step_started — open a Thinking
    // transcript here so the summary doesn't appear with no feedback. It freezes to
    // "Thought for Ns" when the stream ends. Normal discovery followups get theirs from
    // the backend, so skip those.
    if (disambiguationActiveRef.current) {
      disambiguationActiveRef.current = false;
      startThinkingTranscript(
        'discovery',
        'Reviewing your answer and preparing the investigation plan…',
      );
    }
    streamWorkflow(
      createRequestPayload({
        followup_items: updatedFollowups,
      }),
    );
  };

  // A jurisdiction-mismatch confirm option was clicked. Echo the choice and answer the
  // followup with its number ("1"/"2") — the same value a typed reply would send, so the
  // backend resolves it identically. Guarded so a message's buttons fire only once.
  const chooseJurisdiction = (
    message: BOMessage,
    answer: string,
    label: string,
  ) => {
    if (isStreaming || confirmChosen[message.id]) {
      return;
    }
    setConfirmChosen((prev) => ({ ...prev, [message.id]: answer }));
    addMessage({ sender: 'user', text: label });
    // Ensure the "reviewing your answer" Thinking chip opens even for a restored ticket
    // (where the live followup event that normally sets this ref never fired).
    disambiguationActiveRef.current = true;
    answerFollowup(answer);
  };

  const handleSubmit = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    const value = input.trim();
    if (!value || isStreaming || startBlockedByCredits || !userHasAccess) {
      return;
    }

    addMessage({ sender: 'user', text: value });
    setInput('');
    window.setTimeout(() => {
      composerInputRef.current?.focus();
    }, 0);

    // At a phase pause the typed reply is a pause decision, so it must NOT be
    // hijacked by a leftover (unanswered) discovery followup — pendingDecision wins.
    const pendingFollowup = followupItems.some((item) => !item.answer);
    if (pendingFollowup && !pendingDecision) {
      answerFollowup(value);
      return;
    }

    if (finalData) {
      // The post-report options can be TYPED as well as clicked — a bare number
      // (1/2/3) or the option words. Anchored to the whole message so a genuine
      // follow-up QUESTION that merely contains one of these words (e.g. "who are
      // the top 3 owners?", "should I save this?") still goes to the follow-up agent
      // rather than firing an action.
      const choice = value
        .trim()
        .replace(/[.!?]+$/, '')
        .toLowerCase();
      if (/^(1|new|restart|start over|(start (a )?)?new investigation)$/.test(choice)) {
        startOver();
        return;
      }
      if (/^(2|save|save work|view saved( work)?)$/.test(choice)) {
        handleSaveAction();
        return;
      }
      if (/^(3|download|download( the)? report|pdf|get( the)? pdf)$/.test(choice)) {
        downloadReport();
        return;
      }
      // Otherwise it's a question about the completed investigation. The follow-up
      // agent emits no step_started, so open a Thinking transcript here for feedback
      // while it works (the answer handler clears it when the reply arrives).
      startThinkingTranscript('analysis', 'Looking into your question…');
      streamWorkflow(
        createRequestPayload({
          followup_question: value,
          followup_items: [],
        }),
      );
      return;
    }

    // Typed reply at a phase pause → map to the same action as the numbered buttons.
    // The typed text is already shown (added above), so don't echo a second bubble.
    if (pendingDecision) {
      const control = pendingDecision;
      const activePhase = control.active_phase;
      const isDiscoveryGate = activePhase === 'discovery';
      const isLayerPhase =
        activePhase === 'ownership' || activePhase === 'second_layer';
      // The layer-offer message carries this exact control reference + its results.
      const offerMsg = isLayerPhase
        ? messages.find(
            (item) =>
              item.type === 'control' && item.data?.control === pendingDecision,
          )
        : undefined;
      const offerResults = (offerMsg?.data?.results || []) as {
        findings: Finding[];
      }[];
      const isLayerOffer =
        !!offerMsg &&
        offerResults.some((result) => (result.findings || []).length > 0);
      // "2"/"no"/"skip" → the decline/second option; anything else → the primary.
      const saysNo = /\b(2|no|skip|nope|don'?t)\b/i.test(value);

      // Deeper-layer offer. Before "Yes": "2"/no skips + continues, "1"/yes reveals the
      // picklist bubble. After "Yes": "2"/no skips, anything else runs on the current
      // selection (falls back to skip if nothing is ticked).
      if (isLayerOffer && offerMsg) {
        const answeredYes = layerOfferYes[offerMsg.id] === true;
        if (!answeredYes) {
          if (saysNo) {
            sendCommand('next', 'No, continue the workflow', []);
            return;
          }
          revealLayerPicklist(offerMsg);
          return;
        }
        // Step 2 — run on the current selection. Only an explicit decline skips:
        // with an empty tick list as the default, falling through to the skip command
        // would turn a typed "run" into a silent skip of the whole layer.
        if (saysNo) {
          sendCommand('next', 'Skip the deeper search and continue', []);
          return;
        }
        if (selectedEntities.length === 0) {
          addMessage({
            sender: 'bot',
            type: 'question',
            text:
              'Nothing is ticked yet — tick the entities you want the deeper ' +
              'search to cover, or choose "Skip and continue".',
          });
          return;
        }
        sendCommand(
          'next',
          'Run the deeper search on the selected entities',
          selectedEntities,
        );
        return;
      }

      // Discovery gate — users often TYPE the intent instead of clicking (the other
      // gates accept typed replies too). Map the words/number to the same action as each
      // button: background/email/notify → run detached; new/start over/cancel → reset;
      // proceed/walk/live/anything else → interactive.
      // The NUMBER that means "start a new investigation" is 3, matching the three
      // buttons on screen, so typing the number the user can see always does what they
      // see.
      if (isDiscoveryGate) {
        const wantsBackground =
          /\b(2|background|email|notify|later)\b/i.test(value) ||
          /\brun it (and|in)\b/i.test(value) ||
          /\bwalk away\b/i.test(value);
        const wantsNew =
          /\b(new|restart|cancel)\b/i.test(value) ||
          /\bstart over\b/i.test(value) ||
          /\b3\b/.test(value);
        if (wantsBackground) {
          runInBackground(false);
          return;
        }
        if (wantsNew) {
          startOver();
          return;
        }
        sendCommand('next');
        return;
      }

      // Ordinary between-phase pause: 1 = continue, 2 = save.
      if (/\b(2|save)\b/i.test(value)) {
        handleSaveAction();
        return;
      }
      sendCommand('next');
      return;
    }

    if (intakeStep === 'entity') {
      // The entity name is the only thing we collect directly. Every other
      // intake question (country, second-layer search, and any additional
      // clarifiers) is now driven by the discovery agent via followups.
      setEntityName(value);
      startInvestigation({ entity_name: value }, value);
      return;
    }
  };

  const resetWorkflow = () => {
    abortRef.current?.abort();
    resetTicketDraft();
    workflowStateRef.current = {};
    loggedSectionsRef.current = new Set();
    isStreamingRef.current = false;
    lastProgressKeyRef.current = '';
    pendingResultsRef.current = [];
    lastSavedMessageCountRef.current = 0;
    messagesRef.current = [];
    setMessages([]);
    setInput('');
    setEntityName('');
    setCountry('');
    setSecondLayer(false);
    setIntakeStep('entity');
    setIsStreaming(false);
    setBoId('');
    setWorkflowState({});
    setFollowupItems([]);
    setAgentStatuses(AGENTS);
    setAgentFindings({});
    setAgentResultText({});
    setAgentReport(null);
    setExpandedAgents({});
    setPendingDecision(null);
    setSelectedEntities([]);
    setLayerOfferYes({});
    setStepsStatus({});
    setCurrentStep('');
    setActiveAgentKey('');
    setFinalData(null);
    setStreamStage(0);
    setTicketId('');
    setModalOpen(false);
    setNewTicketName('');
    setShareEmail([]);
  };

  // Resume a ticket saved mid-investigation (or after completion) — restores the
  // exact chat transcript plus everything startInvestigation/sendCommand need to
  // keep talking to the backend (bo_id, workflow_state, followup_items).
  const hydrateSavedTicket = (ticket: any) => {
    resetWorkflow();

    const workflowData = Array.isArray(ticket?.workflowData)
      ? ticket.workflowData[0] || {}
      : ticket?.workflowData || {};
    const savedMessages: BOMessage[] = Array.isArray(workflowData.messages)
      ? workflowData.messages
      : [];
    const savedFollowupItems: BOFollowupItem[] = Array.isArray(
      workflowData.followup_items,
    )
      ? workflowData.followup_items
      : [];
    const savedWorkflowState =
      (workflowData.workflow_state as Record<string, unknown>) || {};
    const savedStepsStatus = (workflowData.steps_status || {}) as Record<
      string,
      BOStepStatus
    >;
    const savedResult =
      workflowData.result || ticket?.beneficialOwnership || null;

    setTicketDraft(ticket || {});
    setTicketId(ticket?._id || '');
    setEntityName(workflowData.entity_name || '');
    setCountry(workflowData.country || '');
    setSecondLayer(Boolean(workflowData.second_layer));
    setBoId(workflowData.bo_id || '');
    // So a later auto re-save (see handleSaveAction) preserves who this was
    // already shared with instead of silently clearing it.
    setShareEmail(Array.isArray(ticket?.sharedWith) ? ticket.sharedWith : []);
    setIntakeStep('ready');
    setMessages(savedMessages);
    messagesRef.current = savedMessages;
    // The just-restored transcript IS the last save — nothing "new" yet.
    lastSavedMessageCountRef.current = savedMessages.length;
    setFollowupItems(savedFollowupItems);
    workflowStateRef.current = savedWorkflowState;
    setWorkflowState(savedWorkflowState);
    setFinalData(savedResult);
    setAgentReport(savedResult);
    // The restored final message (if any) should show fully written, not retype
    // itself — see the streamStage gate in renderReportBody.
    setStreamStage(Number.MAX_SAFE_INTEGER);

    // Rebuild the sidebar's per-agent "Results found" detail from the transcript,
    // the same way it's built live: 'agent_result' messages (auto mode) carry
    // their own stage; in step mode the same findings/notes are folded into the
    // next 'control' (or 'layer_picklist') message's data.results instead, tagged
    // by that pause's active_phase (the phase that just finished).
    const extractNote = (raw: string) => {
      const dash = raw.indexOf('—');
      return (dash >= 0 ? raw.slice(dash + 1) : raw).trim();
    };
    const reconstructedFindings: Record<string, Finding[]> = {};
    const reconstructedNotes: Record<string, string> = {};
    savedMessages.forEach((message) => {
      if (message.type === 'agent_result') {
        const agentKey = STAGE_TO_AGENT[message.stage || ''] || '';
        if (!agentKey) {
          return;
        }
        const findings = Array.isArray(message.data?.findings)
          ? message.data.findings
          : [];
        if (findings.length > 0) {
          reconstructedFindings[agentKey] = findings;
        }
        if (typeof message.text === 'string' && message.text) {
          const note = extractNote(message.text);
          if (note) {
            reconstructedNotes[agentKey] = note;
          }
        }
        return;
      }

      if (message.type === 'control' || message.type === 'layer_picklist') {
        const control = message.data?.control as BOControl | undefined;
        const agentKey = control?.active_phase
          ? STAGE_TO_AGENT[control.active_phase] || ''
          : '';
        if (!agentKey) {
          return;
        }
        const results: { summary?: string; findings?: Finding[] }[] =
          Array.isArray(message.data?.results) ? message.data.results : [];
        results.forEach((result) => {
          if (Array.isArray(result.findings) && result.findings.length > 0) {
            reconstructedFindings[agentKey] = result.findings;
          }
          if (typeof result.summary === 'string' && result.summary) {
            const note = extractNote(result.summary);
            if (note) {
              reconstructedNotes[agentKey] = note;
            }
          }
        });
      }
    });
    setAgentFindings(reconstructedFindings);
    setAgentResultText(reconstructedNotes);

    // If the ticket was saved sitting at a phase pause (its numbered Continue/
    // Save/Yes-No buttons still unanswered — the "1. Continue to Final Analysis /
    // 2. Save workflow" screen), that's always the LAST message: nothing else gets
    // added until the user picks an option. Restore it as the pending decision so
    // those buttons — gated on `data.control === pendingDecision` — stay clickable.
    const lastMessage = savedMessages[savedMessages.length - 1];
    if (
      (lastMessage?.type === 'control' ||
        lastMessage?.type === 'layer_picklist') &&
      lastMessage.data?.control
    ) {
      setPendingDecision(lastMessage.data.control);
      // Mirrors the live default when this control/picklist message was first
      // created: nothing is ticked until the user picks.
      setSelectedEntities([]);
    }

    if (Object.keys(savedStepsStatus).length > 0) {
      // Sections already finished in the saved run were logged back then.
      Object.entries(savedStepsStatus).forEach(([stepKey, stepStatus]) => {
        if (statusFromStep(stepStatus) === 'done') {
          loggedSectionsRef.current.add(STEP_TO_AGENT[stepKey]);
        }
      });
      setStepsStatus(savedStepsStatus);
      updateAgentStatusesFromSteps(savedStepsStatus);
    } else if (savedResult) {
      // Older saves have no per-step record — a completed investigation means
      // every real agent ran, so mark them all done rather than leaving the
      // sidebar stuck on "Pending".
      setAgentStatuses(AGENTS.map((agent) => ({ ...agent, status: 'done' })));
    } else {
      // Best-effort reconstruction: any stage a message references got at least
      // as far as running.
      const seenAgentKeys = new Set(
        savedMessages
          .map((message) => STAGE_TO_AGENT[message.stage || ''])
          .filter(Boolean),
      );
      if (seenAgentKeys.size > 0) {
        setAgentStatuses((prev) =>
          prev.map((agent) =>
            seenAgentKeys.has(agent.key) ? { ...agent, status: 'done' } : agent,
          ),
        );
      }
    }
  };

  // Mirrors the fields hydrateSavedTicket sets when resuming a saved ticket --
  // this restores raw component state directly (a captured client-side
  // snapshot), not a server ticket payload.
  const applyRecoveredBoChatState = (state: BOChatRecoverableState) => {
    setMessages(state.messages);
    messagesRef.current = state.messages;
    setInput(state.input);
    setEntityName(state.entityName);
    setCountry(state.country);
    setSecondLayer(state.secondLayer);
    setIntakeStep(state.intakeStep);
    setBoId(state.boId);
    setWorkflowState(state.workflowState);
    workflowStateRef.current = state.workflowState;
    setFollowupItems(state.followupItems);
    setAgentStatuses(state.agentStatuses);
    setAgentFindings(state.agentFindings);
    setAgentResultText(state.agentResultText);
    setAgentReport(state.agentReport);
    setPendingDecision(state.pendingDecision);
    setSelectedEntities(state.selectedEntities);
    setLayerOfferYes(state.layerOfferYes);
    setConfirmChosen(state.confirmChosen);
    setStepsStatus(state.stepsStatus);
    setCurrentStep(state.currentStep);
    setActiveAgentKey(state.activeAgentKey);
    setFinalData(state.finalData);
    setStreamStage(state.streamStage);
    setTicketId(state.ticketId);
    setNewTicketName(state.newTicketName);
    setShareEmail(state.shareEmail);
  };

  // Keeps the recoverable registry pointed at the latest values so a forced
  // re-login redirect (see query-client.ts's handleAuthError) can snapshot
  // whatever's actually on screen at that moment. Written directly during
  // render rather than via a useEffect with a giant dependency array --
  // cheap, and always fresh by the time a capture could possibly happen.
  const boRecoverableStateRef = useRef<BOChatRecoverableState>(
    {} as BOChatRecoverableState,
  );
  boRecoverableStateRef.current = {
    messages: sanitizeBoMessagesForRecovery(messages),
    input: input,
    entityName: entityName,
    country: country,
    secondLayer: secondLayer,
    intakeStep: intakeStep,
    boId: boId,
    workflowState: workflowState,
    followupItems: followupItems,
    agentStatuses: agentStatuses,
    agentFindings: agentFindings,
    agentResultText: agentResultText,
    agentReport: agentReport,
    pendingDecision: pendingDecision,
    selectedEntities: selectedEntities,
    layerOfferYes: layerOfferYes,
    confirmChosen: confirmChosen,
    stepsStatus: stepsStatus,
    currentStep: currentStep,
    activeAgentKey: activeAgentKey,
    finalData: finalData,
    streamStage: streamStage,
    ticketId: ticketId,
    newTicketName: newTicketName,
    shareEmail: shareEmail,
  };

  useEffect(() => {
    return registerRecoverable(
      BO_CHAT_RECOVERY_KEY,
      () => boRecoverableStateRef.current,
    );
  }, []);

  useEffect(() => {
    const resumeTicket = location.state?.resumeBeneficialOwnershipTicket;
    if (resumeTicket && !resumeHydratedRef.current) {
      resumeHydratedRef.current = true;
      hydrateSavedTicket(resumeTicket);
      return;
    }

    const recovered = loadDraft<BOChatRecoverableState>(BO_CHAT_RECOVERY_KEY);
    if (recovered) {
      applyRecoveredBoChatState(recovered);
    }
    // Mount-only: this resumes from router state exactly once when the page is
    // opened via "Continue Workflow"; it must not re-fire on later re-renders.
  }, []);

  const handleCloseModal = () => {
    cancelPendingLeaveSave();
    setModalOpen(false);
  };

  // User-initiated "New / Start over" (header button, in-chat buttons, typed command) —
  // resetWorkflow itself is also called for programmatic resets, which shouldn't log.
  const startOver = () => {
    logAppAction('beneficial_ownership_workflow:start_over');
    resetWorkflow();
  };

  const handleSaveAction = () => {
    if (hasSavedCurrentState) {
      history.push(`/monitor/ticket/${savedTicketId}`);
      return;
    }

    logAppAction('beneficial_ownership_workflow:save_results');

    if (savedTicketId) {
      // Already saved before, just with newer progress since — update that same
      // ticket directly (saveWork falls back to ticketDraft.ticketName) instead
      // of re-running the full "create ticket" wizard for a save that already
      // has a name and sharing settings.
      saveWork();
      return;
    }

    setModalOpen(true);
  };

  const handleSaveTicketFromLeaveModal = () => {
    leaveSaveInProgressRef.current = true;
    setIsLeaveModalOpen(false);
    setModalOpen(true);
  };

  const handleLeaveWithoutSaving = () => {
    setIsLeaveModalOpen(false);
    resetWorkflow();
    continueToPendingLocation();
  };

  const handleLeaveAndOptOut = () => {
    localStorage.setItem(leaveWarningStorageKey, 'true');
    setDisableLeaveWarning(true);
    handleLeaveWithoutSaving();
  };

  const saveWork = async (name?: string): Promise<string | null> => {
    if (!entityName) {
      return null;
    }

    setIsSaving(true);
    const ticketName =
      (name || '').trim() ||
      ticketDraft?.ticketName ||
      newTicketName.trim() ||
      `Beneficial Ownership - ${entityName}`;
    const owner = {
      kc_id: cur_user.basics?.id,
      email: cur_user.basics?.email,
      firstName: cur_user.basics?.firstName,
      lastName: cur_user.basics?.lastName,
    };
    const caseBody: any = {
      ...(ticketDraft?._id || ticketId
        ? { _id: ticketDraft?._id || ticketId }
        : {}),
      owner: owner,
      groupId: cur_user.selected_team.id,
      ticketName: ticketName,
      lastUpdate: formattedDate,
      activityType: 'workflow',
      workflowType: 'beneficial_ownership',
      docType: 'Beneficial Ownership',
      sharedWith: shareEmail,
      status: finalData ? 'Complete' : 'In Progress',
      favorite: false,
      groupName: cur_user.selected_team.name,
      classificationValue: entityName,
      workflowData: [
        {
          bo_id: boId,
          entity_name: entityName,
          country: country,
          // Derived, not the intake answer: a background run turns the second
          // layer on server-side without `secondLayer` ever changing, which is
          // why saved tickets used to report it as off. The intake answer is
          // still passed as the fallback.
          second_layer: didSecondLayerRunFromState(
            workflowStateRef.current || workflowState,
            secondLayer,
          ),
          result: finalData,
          workflow_state: workflowStateRef.current || workflowState,
          followup_items: followupItems,
          messages: messagesRef.current,
          steps_status: stepsStatus,
        },
      ],
      beneficialOwnership: finalData,
      commodity: {
        commodityName: entityName,
        destinationCountry: country,
      },
      activity: {
        user: owner,
        action:
          ticketDraft?._id || ticketId
            ? 'updated beneficial ownership workflow'
            : 'created beneficial ownership workflow',
        timeStamp: Date.now(),
      },
    };

    try {
      const resp = caseBody._id
        ? await CasesAPI.updateCase(caseBody)
        : await CasesAPI.createCase({ newCase: caseBody });

      if (resp?.success) {
        const nextId = resp.result?._id || caseBody._id;
        setTicketId(nextId);
        setTicketDraft({ ...caseBody, _id: nextId });
        lastSavedMessageCountRef.current = messages.length;
        setModalOpen(false);
        setNewTicketName('');
        enqueueSnackbar('Successfully saved beneficial ownership workflow', {
          variant: 'success',
        });
        if (leaveSaveInProgressRef.current) {
          leaveSaveInProgressRef.current = false;
          continueToPendingLocation();
        }
        return nextId;
      }

      setModalOpen(false);
      enqueueSnackbar('Unable to save beneficial ownership workflow.', {
        variant: 'error',
      });
      return null;
    } catch {
      enqueueSnackbar('Unable to save beneficial ownership workflow.', {
        variant: 'error',
      });
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  // "Run & email me": create/refresh the saved-work ticket up front (as In Progress),
  // then hand the investigation to a detached server-side run that will update that
  // ticket to Complete and email the user when the report is ready. The browser is
  // free to leave the moment the acknowledgement (notify_scheduled) arrives.
  const runInBackground = async (echo = true) => {
    // A detached run must not be started mid-stream, or without an entity to
    // investigate — it would create a ticket with nothing to put in it.
    if (isStreaming || !entityName || !userHasAccess) {
      return;
    }
    const ticketIdForRun = await saveWork();
    if (!ticketIdForRun) {
      // saveWork already surfaced the error toast.
      return;
    }
    // Button click has no other echo, so add one; a typed reply is already shown.
    if (echo) {
      addMessage({
        sender: 'user',
        text: 'Run it in the background and email me when it’s done',
      });
    }
    const recipientName = [cur_user.basics?.firstName, cur_user.basics?.lastName]
      .filter(Boolean)
      .join(' ');
    streamWorkflow(
      createRequestPayload({
        command: { action: 'run_all' },
        notify_on_complete: true,
        notify: {
          ticket_id: ticketIdForRun,
          ticket_name:
            ticketDraft?.ticketName || `Beneficial Ownership - ${entityName}`,
          recipient_email: cur_user.basics?.email,
          recipient_name: recipientName,
          // Keycloak id drives the in-app (bell) notification's recipient filter.
          recipient_kc_id: cur_user.basics?.id,
          group_id: cur_user.selected_team?.id,
          group_name: cur_user.selected_team?.name,
        },
      }),
    );
  };

  const buildPdfReportData = () => {
    const data = finalData || {};
    const savedWorkflowData = Array.isArray(ticketDraft?.workflowData)
      ? ticketDraft.workflowData[0] || {}
      : ticketDraft?.workflowData || {};
    const currentWorkflowState = workflowStateRef.current || workflowState;

    return {
      ticketName:
        ticketDraft?.ticketName ||
        `Beneficial Ownership - ${entityName || 'Investigation'}`,
      entityName: entityName,
      country: country,
      secondLayer: didSecondLayerRunFromState(
        workflowStateRef.current || workflowState,
        secondLayer,
      ),
      sanctionsFlag: data.sanctions_flag || '',
      sanctionsSummary: data.sanctions_summary || '',
      executiveSummary: data.executive_summary || '',
      summary:
        data.general_description ||
        data.summary ||
        data.beneficial_ownership_summary ||
        '',
      owners: Array.isArray(data.owners) ? data.owners : [],
      keyFindings: Array.isArray(data.key_findings) ? data.key_findings : [],
      associatedEntities: Array.isArray(data.associated_entities)
        ? data.associated_entities
        : [],
      supportingLinks: Array.isArray(data.supporting_links)
        ? data.supporting_links
        : [],
      ownershipGraph: buildOwnershipGraph({
        classificationValue: entityName,
        beneficialOwnership: data,
        workflowData: {
          ...savedWorkflowData,
          entity_name: entityName,
          country: country,
          workflow_state: currentWorkflowState,
        },
      }),
      preparedBy:
        [cur_user?.basics?.firstName, cur_user?.basics?.lastName]
          .filter(Boolean)
          .join(' ')
          .trim() ||
        cur_user?.basics?.email ||
        'BITE Beneficial Ownership',
    };
  };

  const downloadReport = async () => {
    if (!finalData) {
      return;
    }

    const safeName = entityName.replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    const pdfName = `beneficial-ownership-${safeName || 'report'}.pdf`;
    try {
      await screeningTicketBeneficialOwnershipPDF(
        buildPdfReportData(),
        pdfName,
      );
    } catch (error: any) {
      enqueueSnackbar(
        `Failed to generate report PDF: ${error?.message || 'Unknown error'}`,
        { variant: 'error' },
      );
    }
  };

  const getAgentStepDetails = (agentKey: string) =>
    (AGENT_STEP_ORDER[agentKey] || [])
      .map((stepKey) => ({
        key: stepKey,
        label: getStepLabel(stepKey),
        status: statusFromStep(stepsStatus[stepKey] || {}),
      }))
      .filter((step) => stepsStatus[step.key] || step.key === currentStep);

  const getAgentDetailText = (agent: AgentStatus) => {
    const stepDetails = getAgentStepDetails(agent.key);
    const runningStep = stepDetails.find((step) => step.status === 'running');
    const latestStep = [...stepDetails]
      .reverse()
      .find((step) => step.status !== 'pending');

    if (currentStep && STEP_TO_AGENT[currentStep] === agent.key) {
      return getStepLabel(currentStep);
    }

    if (runningStep) {
      return runningStep.label;
    }

    if (latestStep && agent.status !== 'pending') {
      return latestStep.label;
    }

    return getStatusText(agent.status);
  };

  const renderLinkedLabel = (
    label: string,
    href?: string,
    fallback = 'Unknown',
  ) => {
    const safeHref = safeUrl(href);
    if (!safeHref) {
      return label || fallback;
    }

    return (
      <a href={safeHref} target='_blank' rel='noopener noreferrer'>
        {label || fallback}
      </a>
    );
  };

  // Renders the phase -> group -> step -> finding hierarchy for one phase's Thinking
  // transcript. Shared by the live block and the settled "Thought for Ns" block, so the
  // two can never drift apart; live lines additionally get a one-shot fade-in.
  //
  // Classes come from standard-workflow.css (already loaded via StandardWorkflowLayout),
  // the same stylesheet the Export workflow's thinking transcript uses — so both tools
  // look identical and a change to one lands on both.
  const renderThoughtStepEntries = (
    steps: BOThoughtStep[],
    keyPrefix: string,
    isLive: boolean,
  ) => {
    const liveLineClass = isLive ? ' standard-workflow-thought-line-in' : '';

    return steps.map((step, index) => {
      const key = `${keyPrefix}-${index}`;

      if (step.level === 'phase') {
        return (
          <div
            className={`standard-workflow-thought-goal${liveLineClass}`}
            key={key}
          >
            {step.text}
          </div>
        );
      }

      if (step.level === 'group') {
        return (
          <div
            className={`standard-workflow-thought-group${liveLineClass}`}
            key={key}
          >
            {step.text}
          </div>
        );
      }

      if (step.level === 'finding') {
        // A DETERMINATION. Reuses the SAME row markup and class as a progress step so
        // the dot is identical (and stays in sync if the step dot ever changes); the
        // extra class only lifts the TEXT contrast so it still reads as a conclusion.
        return (
          <div
            className={`standard-workflow-thought-step standard-workflow-thought-finding${liveLineClass}`}
            key={key}
          >
            <span aria-hidden='true' />
            <div>{step.text}</div>
          </div>
        );
      }

      return (
        <div
          className={`standard-workflow-thought-step${liveLineClass}`}
          key={key}
        >
          <span aria-hidden='true' />
          <div>{step.text}</div>
        </div>
      );
    });
  };

  const renderThinkingTranscript = (message: BOMessage) => {
    const thinking = message.thinking;
    if (!thinking) {
      return null;
    }

    // `elapsedMs` is only stamped once the transcript freezes (see
    // finalizeRunningThinking); while running it's still 0, so clock it live off
    // `startedAt` against `nowMs`, the 1s-interval state whose update is what re-runs
    // this render in the first place.
    const liveElapsedMs = thinking.running
      ? nowMs - thinking.startedAt
      : thinking.elapsedMs;
    const seconds = Math.max(1, Math.round(liveElapsedMs / 1000));
    const durationLabel = thinking.running
      ? seconds < 2
        ? 'Thinking'
        : `Thinking for ${seconds}s`
      : seconds < 2
      ? 'Thought for a moment'
      : `Thought for ${seconds}s`;

    const heading = AGENT_THOUGHT_HEADING[thinking.agentKey];
    // Tickets saved BEFORE this transcript gained levels hold a flat `searches:
    // string[]` and no `steps` (saveWork persists message objects verbatim, and
    // loadFromTicket restores them verbatim), so spreading `thinking.steps` blind
    // would throw on reopening one and take the whole page with it. Map the legacy
    // strings to plain step lines instead.
    const legacyThinking = thinking as BOThinking & { searches?: string[] };
    const thoughtSteps: BOThoughtStep[] = Array.isArray(thinking.steps)
      ? thinking.steps
      : (legacyThinking.searches || []).map((text) => ({
          text: text,
          level: 'step' as BOThoughtLevel,
        }));
    // The phase's own step_started line is its overview, hoisted above the appended
    // lines so the user always has the "what is this phase for" context in view.
    const entries: BOThoughtStep[] = [
      ...(thinking.header
        ? [{ text: thinking.header, level: 'phase' as BOThoughtLevel }]
        : []),
      ...thoughtSteps,
      // The final-analysis model streams its own reasoning as token_delta; show the
      // tail of it as one live line rather than letting an unbounded blob push the
      // transcript off screen.
      ...(thinking.thinkingText
        ? [
            {
              text: thinking.thinkingText.slice(-240),
              level: 'step' as BOThoughtLevel,
            },
          ]
        : []),
    ];

    return (
      <div className='standard-workflow-thought-block' key={message.id}>
        {heading && (
          <div className='standard-workflow-thought-phase-heading standard-workflow-thought-phase-title'>
            {heading}
          </div>
        )}
        <div
          className={`standard-workflow-thought-summary standard-workflow-thought-summary-${
            thinking.running ? 'live' : 'static'
          }`}
          aria-hidden={thinking.running ? 'true' : undefined}
        >
          {durationLabel}
        </div>
        <div className='standard-workflow-thought-details'>
          {renderThoughtStepEntries(
            entries,
            String(message.id),
            thinking.running,
          )}
          {thinking.running ? (
            <div className='standard-workflow-thought-step standard-workflow-thought-step-thinking'>
              <span aria-hidden='true' />
              <div>Thinking…</div>
            </div>
          ) : thinking.completed ? (
            <div className='standard-workflow-thought-step standard-workflow-thought-step-done'>
              <span aria-hidden='true' />
              <div>Done</div>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const renderStreamingText = (text: string | JSX.Element) => {
    if (typeof text !== 'string') {
      return text;
    }

    const streamingMatch = text.match(/^(.*?)(\.\.\.)$/);
    if (!streamingMatch) {
      return text;
    }

    const [, content] = streamingMatch;

    return (
      <>
        {content}
        <span className='bo-streaming-dots' aria-hidden='true'>
          {[0, 1, 2].map((dotIndex) => (
            <span
              key={dotIndex}
              className='bo-streaming-dot'
              style={{ animationDelay: `${dotIndex * 0.18}s` }}
            >
              .
            </span>
          ))}
        </span>
      </>
    );
  };

  // Bucket findings by their optional `group` label, preserving first-seen order.
  const groupFindings = (findings: Finding[]) => {
    const groups: { label: string | null; items: Finding[] }[] = [];
    findings.forEach((finding) => {
      const label = finding.group || null;
      let bucket = groups.find((group) => group.label === label);
      if (!bucket) {
        bucket = { label: label, items: [] };
        groups.push(bucket);
      }
      bucket.items.push(finding);
    });
    return groups;
  };

  const renderFindingTitle = (finding: Finding, className: string) =>
    safeUrl(finding.url) ? (
      <a
        className={className}
        href={safeUrl(finding.url)}
        target='_blank'
        rel='noopener noreferrer'
      >
        {finding.title}
      </a>
    ) : (
      <span className={className}>{finding.title}</span>
    );

  // Auto-mode agent result: a brief flush status line in the conversation. The full
  // findings live in the progress sidebar (Export's design — bubbles stay clean text).
  // Flush (borderless) result block shown inside a chat bubble: a summary line then
  // the finding rows. Matches Export's clean bubble typography (no card/icon), but —
  // unlike Export — we DO surface results in the chat as well as the sidebar.
  const renderPauseResults = (
    resultBlocks: { summary: string | JSX.Element; findings: Finding[] }[],
    keyPrefix: string,
  ) =>
    resultBlocks.length === 0 ? null : (
      <div className='bo-pause-results'>
        {resultBlocks.map((result, resultIndex) => {
          // Group findings by their source (finding.group, e.g. the record table)
          // so records are listed under the table they came from — preserving order.
          const groups: Array<{ label?: string; items: Finding[] }> = [];
          result.findings.forEach((finding) => {
            const existing = groups.find(
              (g) => (g.label || '') === (finding.group || ''),
            );
            if (existing) {
              existing.items.push(finding);
            } else {
              groups.push({ label: finding.group, items: [finding] });
            }
          });
          return (
            <div
              key={`${keyPrefix}-${resultIndex}`}
              className='bo-pause-result'
            >
              {result.summary && (
                <div className='bo-pause-result-summary'>{result.summary}</div>
              )}
              {groups.map((group, groupIndex) => (
                <div
                  key={`${keyPrefix}-${resultIndex}-g${groupIndex}`}
                  className='bo-pause-result-group'
                >
                  {group.label && (
                    <div className='bo-pause-result-group-label'>
                      {group.label}
                    </div>
                  )}
                  <div className='bo-pause-result-list'>
                    {group.items.map((finding, index) => (
                      <div
                        key={`${keyPrefix}-${resultIndex}-g${groupIndex}-${index}`}
                        className='bo-pause-result-row'
                      >
                        {renderFindingTitle(finding, 'bo-pause-result-title')}
                        {finding.detail && (
                          <div className='bo-pause-result-detail'>
                            {finding.detail}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    );

  // Auto-mode agent result (only reachable if run_mode is "auto"): flush summary +
  // findings in the conversation.
  const renderAgentResult = (message: BOMessage) =>
    renderPauseResults(
      [
        {
          summary: message.text,
          findings: (message.data?.findings || []) as Finding[],
        },
      ],
      `a${message.id}`,
    );

  // The separate picklist bubble that appears BELOW a layer offer once the user picks
  // "Yes": the tick list (all pre-selected) + Run / Skip. Carries the same control ref
  // so its Run/Skip dispatch is actionable while the pause is still pending.
  const renderLayerPicklist = (message: BOMessage) => {
    const data = (message.data || {}) as {
      control?: BOControl;
      results?: { summary: string; findings: Finding[] }[];
    };
    const control = (data.control || {}) as BOControl;
    const results = data.results || [];
    const isSecond = control.active_phase === 'ownership';
    const layerName = isSecond ? 'second-layer' : 'third-layer';
    const subject = isSecond ? 'owners' : 'connections';
    const actionable =
      !isStreaming && !!data.control && data.control === pendingDecision;

    // Deduped: the same person can surface via two different entities, and the server
    // does not dedupe `selected_entities` (it only trims blanks), so a repeated title
    // would be searched twice and inflate the deeper layer's fan-out. Also keeps the
    // "Select all (n)" count and its disabled comparison honest.
    const allTitles = Array.from(
      new Set(
        results.flatMap((result) =>
          result.findings.map((finding) => finding.title),
        ),
      ),
    );

    return (
      <>
        <div className='bo-select-hint'>
          Tick the {subject} you&apos;d like to include — nothing is selected by
          default, and the {layerName} search covers only what you tick.
          {actionable && allTitles.length > 0 && (
            <span className='bo-select-bulk'>
              <button
                type='button'
                className='bo-select-bulk-action'
                disabled={selectedEntities.length === allTitles.length}
                onClick={() => setSelectedEntities(allTitles)}
              >
                Select all ({allTitles.length})
              </button>
              <button
                type='button'
                className='bo-select-bulk-action'
                disabled={selectedEntities.length === 0}
                onClick={() => setSelectedEntities([])}
              >
                Clear
              </button>
            </span>
          )}
        </div>
        <div className='bo-select-list'>
          {results.flatMap((result, resultIndex) =>
            result.findings.map((finding, index) => (
              <label key={`${resultIndex}-${index}`} className='bo-select-row'>
                <input
                  type='checkbox'
                  checked={selectedEntities.includes(finding.title)}
                  disabled={!actionable}
                  onChange={() => toggleEntitySelection(finding.title)}
                />
                <span className='bo-select-row-body'>
                  <span className='bo-select-row-title'>{finding.title}</span>
                  {finding.detail && (
                    <span className='bo-select-row-detail'>
                      {finding.detail}
                    </span>
                  )}
                </span>
              </label>
            )),
          )}
        </div>
        <ol className='options-container workflow-control-options'>
          <li>
            <button
              type='button'
              className='option-button bo-layer-run'
              disabled={!actionable || selectedEntities.length === 0}
              onClick={() =>
                sendCommand(
                  'next',
                  `Run the ${layerName} search on the selected ${subject}`,
                  selectedEntities,
                )
              }
            >
              Run the {layerName} search on the selected {subject}
            </button>
          </li>
          <li>
            <button
              type='button'
              className='option-button'
              disabled={!actionable}
              onClick={() =>
                sendCommand(
                  'next',
                  `Skip the ${layerName} search and continue`,
                  [],
                )
              }
            >
              Skip and continue the workflow
            </button>
          </li>
        </ol>
      </>
    );
  };

  // Jurisdiction-mismatch confirm: the question text + numbered option buttons, matching
  // the decision-gate pattern (.options-container .workflow-control-options / .option-button).
  // Clicking answers the underlying followup with the option's number; typing "1"/"2"
  // works too. Once picked, the chosen option shows selected and the other goes muted.
  const renderJurisdictionConfirm = (message: BOMessage) => {
    const data = (message.data || {}) as {
      options?: { answer: string; label: string }[];
    };
    const options = Array.isArray(data.options) ? data.options : [];
    const chosen = confirmChosen[message.id];
    const actionable = !isStreaming && !chosen;

    return (
      <>
        <div className='bo-pause-text'>{message.text}</div>
        <ol className='options-container workflow-control-options'>
          {options.map((option) => {
            if (chosen) {
              return (
                <li key={option.answer}>
                  <span
                    className={`option-button ${
                      chosen === option.answer
                        ? 'bo-option-chosen'
                        : 'bo-option-muted'
                    }`}
                  >
                    {option.label}
                  </span>
                </li>
              );
            }
            return (
              <li key={option.answer}>
                <button
                  type='button'
                  className='option-button'
                  disabled={!actionable}
                  onClick={() =>
                    chooseJurisdiction(message, option.answer, option.label)
                  }
                >
                  {option.label}
                </button>
              </li>
            );
          })}
        </ol>
      </>
    );
  };

  // Checkpoint / layer-offer controls: flush results + flush prompt text + Export's
  // numbered option list (.options-container .workflow-control-options / .option-button,
  // styled by standard-workflow.css under .standard-workflow-conversation).
  const renderPauseControls = (message: BOMessage) => {
    const data = (message.data || {}) as {
      control?: BOControl;
      results?: { summary: string; findings: Finding[] }[];
      discovery_summary?: {
        entity_name?: string;
        country?: string;
        focus?: string;
      };
    };
    const control = (data.control || {}) as BOControl;
    const results = data.results || [];
    const discoverySummary = data.discovery_summary;
    const actions = control.available_actions || [];
    const nextLabel =
      (control.next_phase && control.phase_labels?.[control.next_phase]) ||
      'the next step';
    const isDiscoveryGate = control.active_phase === 'discovery';
    // Only the most recent, still-unanswered pause is actionable (identity match).
    const actionable =
      !isStreaming && !!data.control && data.control === pendingDecision;

    // Deeper-layer offer: ownership pause offers a SECOND layer; second-layer pause
    // offers a THIRD (final) layer. Only offered when the prior step found entities.
    const hasFindings = results.some(
      (result) => (result.findings || []).length > 0,
    );
    const isLayerOffer =
      (control.active_phase === 'ownership' ||
        control.active_phase === 'second_layer') &&
      hasFindings;

    if (isLayerOffer) {
      const isSecond = control.active_phase === 'ownership';
      const layerName = isSecond ? 'second-layer' : 'third-layer';
      const phaseLabel =
        (control.active_phase &&
          control.phase_labels?.[control.active_phase]) ||
        (isSecond ? 'Ownership Research' : 'Second-Layer Search');
      const explainText = isSecond
        ? 'A second-layer search follows each owner one hop further — surfacing their other companies, who else owns them, and any sanctions.'
        : 'A third-layer search follows each selected connection one hop deeper.';
      // The offer bubble stays PUT: report + question + Yes/No. Picking "Yes" is a
      // client-only action that appends a SEPARATE picklist bubble below (see
      // revealLayerPicklist) — the workflow's response to the choice — rather than
      // mutating this bubble. Once answered, the choices here go inert.
      const answeredYes = layerOfferYes[message.id] === true;

      return (
        <>
          <div className='bo-pause-announce'>{phaseLabel} is complete.</div>
          {renderPauseResults(results, `p${message.id}`)}
          <div className='bo-pause-text'>
            {explainText} Would you like to do a {layerName} research?
          </div>
          <ol className='options-container workflow-control-options'>
            <li>
              {answeredYes ? (
                <span className='option-button bo-option-chosen'>
                  Yes, do {layerName} research
                </span>
              ) : (
                <button
                  type='button'
                  className='option-button'
                  disabled={!actionable}
                  onClick={() => revealLayerPicklist(message)}
                >
                  Yes, do {layerName} research
                </button>
              )}
            </li>
            <li>
              {answeredYes ? (
                <span className='option-button bo-option-muted'>
                  No, continue the workflow
                </span>
              ) : (
                <button
                  type='button'
                  className='option-button'
                  disabled={!actionable}
                  onClick={() =>
                    sendCommand(
                      'next',
                      `Skip the ${layerName} search and continue`,
                      [],
                    )
                  }
                >
                  No, continue the workflow
                </button>
              )}
            </li>
          </ol>
        </>
      );
    }

    // Discovery gate + ordinary between-phase pauses. Every checkpoint pauses and
    // offers Continue + Save (no End) so the user drives each step; the discovery gate
    // instead offers Proceed + Start New Investigation.
    // Buttons carry the intent (short + scannable); the prompt above carries the
    // detail (how long each takes / that we'll email the ticket).
    // "Walk me through it live" reads as a choice because it sits next to the background
    // option.
    const nextButtonLabel = isDiscoveryGate
      ? 'Walk me through it live'
      : `Continue to ${nextLabel}`;

    return (
      <>
        {renderPauseResults(results, `p${message.id}`)}
        {isDiscoveryGate && discoverySummary && (
          <>
            <div className='bo-pause-text'>
              Here’s what I have for this investigation:
            </div>
            <dl className='bo-discovery-summary'>
              <div>
                <dt>Entity name</dt>
                <dd>{discoverySummary.entity_name || '—'}</dd>
              </div>
              <div>
                <dt>Country / Jurisdiction</dt>
                <dd>{discoverySummary.country || 'none specified'}</dd>
              </div>
              {discoverySummary.focus ? (
                <div>
                  <dt>Focus area</dt>
                  <dd>{discoverySummary.focus}</dd>
                </div>
              ) : null}
            </dl>
          </>
        )}
        <div className='bo-pause-text'>{message.text}</div>
        <ol className='options-container workflow-control-options'>
          {actions.includes('next') && (
            <li>
              <button
                type='button'
                className='option-button'
                disabled={!actionable}
                onClick={() => sendCommand('next', nextButtonLabel)}
              >
                {nextButtonLabel}
              </button>
            </li>
          )}
          {isDiscoveryGate && (
            <li>
              {/* Detached background run: saves the ticket now and emails the user the
                  finished report, so they don't have to watch the whole investigation. */}
              <button
                type='button'
                className='option-button'
                disabled={!actionable}
                onClick={() => runInBackground()}
              >
                Run in the background
              </button>
            </li>
          )}
          {isDiscoveryGate ? (
            <li>
              <button
                type='button'
                className='option-button'
                disabled={!actionable}
                onClick={startOver}
              >
                Start a new investigation
              </button>
            </li>
          ) : (
            <li>
              <button
                type='button'
                className='option-button'
                disabled={!actionable}
                onClick={handleSaveAction}
              >
                {hasSavedCurrentState ? 'View saved workflow' : 'Save workflow'}
              </button>
            </li>
          )}
        </ol>
      </>
    );
  };

  // Report body (no action buttons) — shared by the chat final card and the
  // Final Analysis sidebar card.
  const renderReportBody = (data: any, animate = false) => {
    const findings = Array.isArray(data?.key_findings) ? data.key_findings : [];
    const owners = Array.isArray(data?.owners) ? data.owners : [];
    const associated = Array.isArray(data?.associated_entities)
      ? data.associated_entities
      : [];
    const links = Array.isArray(data?.supporting_links)
      ? data.supporting_links
      : [];

    // The report used to animate its free-text blocks in parallel (jumbled,
    // uneven typing) while every list below "Key findings" popped in statically.
    // Now everything shares one sequence, numbered in render order: text blocks
    // type word-by-word (renderStreamSegment); list rows that have no free text
    // just wait their turn and fade in (renderRevealSegment). Each segment's
    // onComplete/onRevealed advances streamStage, unlocking the next.
    // Stage numbers must stay stable across renders, so every allocation below
    // runs unconditionally (never skipped by an early return) even when its
    // content isn't ready to display yet.
    let segmentCount = 0;
    const nextStage = () => {
      const stage = segmentCount;
      segmentCount += 1;
      return stage;
    };

    const renderStreamSegment = (
      text: string,
      wrap: (node: React.ReactNode) => React.ReactElement,
    ) => {
      const stage = nextStage();
      if (animate && stage > streamStage) {
        return null;
      }
      const isCurrent = animate && stage === streamStage;
      return wrap(
        <WorkflowStreamingResponse
          text={text}
          animate={isCurrent}
          onComplete={
            isCurrent
              ? () => setStreamStage((prev) => Math.max(prev, stage + 1))
              : undefined
          }
        />,
      );
    };

    const renderRevealSegment = (key: React.Key, node: React.ReactNode) => {
      const stage = nextStage();
      const ready = !animate || stage <= streamStage;
      if (!ready) {
        return null;
      }
      const advancing = animate && stage === streamStage;
      return (
        <WorkflowSequentialReveal
          key={key}
          ready={ready}
          advancing={advancing}
          onRevealed={() => setStreamStage((prev) => Math.max(prev, stage + 1))}
        >
          {node}
        </WorkflowSequentialReveal>
      );
    };

    return (
      <>
        <h4>Sanctions: {data?.sanctions_flag || 'not confirmed'}</h4>
        {renderStreamSegment(
          data?.general_description || data?.summary || data?.message || '',
          (node) => (
            <p>{node}</p>
          ),
        )}
        {data?.sanctions_summary &&
          renderStreamSegment(data.sanctions_summary, (node) => <p>{node}</p>)}

        {findings.length > 0 && (
          <>
            {renderStreamSegment('Key findings', (node) => (
              <h4>{node}</h4>
            ))}
            <ul>
              {findings.map((finding: any, index: number) => {
                const revealStage = nextStage();
                const ready = !animate || revealStage <= streamStage;
                const advancing = animate && revealStage === streamStage;
                // Always allocate the nested paragraph's stage (and build its
                // node) before checking `ready`, so numbering downstream never
                // shifts depending on what's visible on a given render.
                const findingParagraph = finding.finding
                  ? renderStreamSegment(finding.finding, (node) => (
                      <p>{node}</p>
                    ))
                  : null;

                if (!ready) {
                  return null;
                }

                return (
                  <WorkflowSequentialReveal
                    key={`${finding.title || 'finding'}-${index}`}
                    ready={ready}
                    advancing={advancing}
                    onRevealed={() =>
                      setStreamStage((prev) => Math.max(prev, revealStage + 1))
                    }
                  >
                    <li>
                      <strong>
                        {renderLinkedLabel(
                          finding.title || 'Finding',
                          finding.source_url,
                        )}
                      </strong>
                      {finding.risk_level && (
                        <span> - {finding.risk_level}</span>
                      )}
                      {findingParagraph}
                    </li>
                  </WorkflowSequentialReveal>
                );
              })}
            </ul>
          </>
        )}

        {owners.length > 0 && (
          <>
            {renderStreamSegment('Ownership found', (node) => (
              <h4>{node}</h4>
            ))}
            <ul>
              {owners.map((ownerItem: any, index: number) =>
                renderRevealSegment(
                  `${ownerItem.name || 'owner'}-${index}`,
                  <li>
                    <strong>
                      {renderLinkedLabel(ownerItem.name, ownerItem.source_url)}
                    </strong>
                    <span>
                      {ownerItem.relationship_to_subject
                        ? ` - ${ownerItem.relationship_to_subject}`
                        : ''}
                      {ownerItem.ownership_percentage
                        ? `, ${ownerItem.ownership_percentage}%`
                        : ''}
                      {ownerItem.sanctions_flag &&
                      ownerItem.sanctions_flag !== 'none'
                        ? `, sanctions: ${ownerItem.sanctions_flag}`
                        : ''}
                    </span>
                  </li>,
                ),
              )}
            </ul>
          </>
        )}

        {associated.length > 0 && (
          <>
            {renderStreamSegment('Associated entities', (node) => (
              <h4>{node}</h4>
            ))}
            <ul>
              {associated.map((entity: any, index: number) =>
                renderRevealSegment(
                  `${entity.name || 'entity'}-${index}`,
                  <li>
                    <strong>
                      {renderLinkedLabel(entity.name, entity.source_url)}
                    </strong>
                    {entity.relation && <span> - {entity.relation}</span>}
                  </li>,
                ),
              )}
            </ul>
          </>
        )}

        {links.length > 0 && (
          <>
            {renderStreamSegment('Supporting links', (node) => (
              <h4>{node}</h4>
            ))}
            <ul>
              {links.map((link: any, index: number) =>
                renderRevealSegment(
                  `${link.title || 'source'}-${index}`,
                  <li>
                    {renderLinkedLabel(link.title || 'Source', link.url)}
                    {link.supports && <span> - {link.supports}</span>}
                  </li>,
                ),
              )}
            </ul>
          </>
        )}
      </>
    );
  };

  const renderFinalSummary = (data: any, animate = false) => (
    <div className='bo-final-card'>
      <div className='bo-pause-announce'>
        {entityName
          ? `${entityName} investigation complete:`
          : 'Investigation complete:'}
      </div>
      <div className='bo-final-report'>{renderReportBody(data, animate)}</div>
      {/* Same numbered-option pattern as every other stage (no floating buttons). */}
      <ol className='options-container workflow-control-options'>
        <li>
          <button
            type='button'
            className='option-button'
            onClick={startOver}
          >
            Start new investigation
          </button>
        </li>
        <li>
          <button
            type='button'
            className='option-button'
            onClick={handleSaveAction}
            disabled={isSaving}
          >
            {hasSavedCurrentState ? 'View saved work' : 'Save work'}
          </button>
        </li>
        <li>
          <button
            type='button'
            className='option-button'
            onClick={downloadReport}
          >
            Download report
          </button>
        </li>
      </ol>
    </div>
  );

  // ---- Progress sidebar (develop's WorkflowProgressTimeline pattern) ----
  const renderAgentProgressSteps = (
    stepDetails: ReturnType<typeof getAgentStepDetails>,
    showStatus: boolean,
  ) => (
    <div className={`bo-progress-agent-steps${showStatus ? '' : ' compact'}`}>
      {stepDetails.map((step) => (
        <div className={`bo-progress-agent-step ${step.status}`} key={step.key}>
          <span className='bo-progress-agent-step-dot' aria-hidden='true' />
          <span className='bo-progress-agent-step-label'>{step.label}</span>
          {showStatus && (
            <span className='bo-progress-agent-step-status'>
              {step.status === 'done'
                ? 'Complete'
                : step.status === 'running'
                ? 'In progress'
                : step.status === 'error'
                ? 'Needs attention'
                : 'Pending'}
            </span>
          )}
        </div>
      ))}
    </div>
  );

  // Our richer per-agent output (live findings, result note, final report) rendered
  // inside the timeline step's expanded body — improvements kept, develop's design.
  const renderAgentFindings = (agentKey: string) => {
    const findingItems = agentFindings[agentKey] || [];
    const resultNote = agentResultText[agentKey] || '';
    const report = agentKey === 'analysis' ? agentReport : null;
    if (findingItems.length === 0 && !resultNote && !report) {
      return null;
    }
    return (
      <div className='bo-agent-detail'>
        {findingItems.length > 0 && (
          <div className='bo-agent-findings'>
            <div className='bo-agent-activity-label'>Results found</div>
            {groupFindings(findingItems).map((group, groupIndex) => (
              <div
                key={`${agentKey}-group-${groupIndex}`}
                className='bo-agent-finding-group'
              >
                {group.label && (
                  <div className='bo-agent-finding-group-label'>
                    {group.label}
                  </div>
                )}
                {group.items.map((finding, index) => (
                  <div
                    key={`${agentKey}-finding-${groupIndex}-${index}`}
                    className='bo-agent-finding-item'
                  >
                    {renderFindingTitle(finding, 'bo-agent-finding-title')}
                    {finding.detail && (
                      <div className='bo-agent-finding-detail'>
                        {finding.detail}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
        {findingItems.length === 0 && resultNote && (
          <div className='bo-agent-findings'>
            <div className='bo-agent-activity-label'>Result</div>
            <div className='bo-agent-result-note'>{resultNote}</div>
          </div>
        )}
        {report && (
          <div className='bo-agent-report'>
            <div className='bo-agent-activity-label'>Full report</div>
            {renderReportBody(report)}
          </div>
        )}
      </div>
    );
  };

  const boProgressSteps: WorkflowProgressStep[] = agentStatuses.map((agent) => {
    const stepDetails = getAgentStepDetails(agent.key);
    const findings = renderAgentFindings(agent.key);
    const status: WorkflowStepStatus =
      agent.status === 'done'
        ? 'complete'
        : agent.status === 'running'
        ? 'running'
        : agent.status === 'error'
        ? 'error'
        : agent.status === 'skipped'
        ? 'skipped'
        : 'pending';
    return {
      label: agent.label,
      value:
        agent.status === 'done'
          ? 'Complete'
          : agent.status === 'running'
          ? 'In progress'
          : agent.status === 'error'
          ? 'Needs attention'
          : agent.status === 'skipped'
          ? 'Skipped'
          : 'Pending',
      detail: getAgentDetailText(agent),
      status: status,
      collapsedBody:
        stepDetails.length > 0
          ? renderAgentProgressSteps(stepDetails, false)
          : undefined,
      expandedBody:
        stepDetails.length > 0 || findings ? (
          <>
            {stepDetails.length > 0 &&
              renderAgentProgressSteps(stepDetails, true)}
            {findings}
          </>
        ) : undefined,
    };
  });

  if (!cur_user.selected_team) {
    return <Redirect to='/' />;
  }

  return (
    <>
      <Prompt when={shouldWarnOnLeave} message={handleBlockedNavigation} />
      <WorkflowPageHeader
        title='Beneficial Ownership'
        description='Investigate ownership structures, connected entities and sanctions risk in one guided workflow.'
        newActionLabel='New Investigation'
        extraActions={<CreditMeterButton packType='credits' size='sm' />}
        onNew={startOver}
        onSave={handleSaveAction}
        savedPath={
          hasSavedCurrentState ? `/monitor/ticket/${savedTicketId}` : undefined
        }
        disableNew={isStreaming || trialLimitReached || !userHasAccess}
        disableSave={
          !entityName ||
          isStreaming ||
          isSaving ||
          trialLimitReached ||
          !userHasAccess
        }
      />
      <WorkflowNotice>
        <p>
          Beneficial ownership research is provided for general compliance and
          due-diligence planning. Results may be incomplete and should be
          independently verified against authoritative corporate, sanctions and
          regulatory records before making compliance or transaction decisions.
        </p>
      </WorkflowNotice>
      {startBlockedByCredits && (
        <WorkflowCreditWarning
          remaining={remainingCredits}
          cost={BENEFICIAL_OWNERSHIP_CREDIT_COST}
        />
      )}
      <div
        ref={workflowGridRef}
        className='standard-workflow-grid bo-workflow-shell chat-workflow-row'
      >
        <section className='standard-workflow-assistant-card bo-chat-pane'>
          <WorkflowAssistantHeader
            title='Ownership Assistant'
            status={
              trialLimitReached
                ? 'unavailable'
                : agentStatuses.some((agent) => agent.status === 'error')
                ? 'error'
                : isStreaming
                ? 'running'
                : 'idle'
            }
          />
          {!userHasAccess && (
          <RequiredLicense
            userLicense={
              (['none', 'standard', 'pro', 'business', 'enterprise'].includes(
                cur_user?.license,
              )
                ? cur_user.license
                : 'none') as LicenseType
            }
            minimumLicense='business'
          />
          )}
          {trialLimitReached ? (
            <TrialRunUsedPanel workflowName='Beneficial Ownership' />
          ) : messages.length > 0 ? (
            <>
              <div
                ref={chatBodyRef}
                className='bo-chat-body standard-workflow-chat-scroll standard-workflow-conversation'
              >
                {messages.map((message, messageIndex) =>
                  message.type === 'thinking' ? (
                    <div key={message.id} className='chat-message bot'>
                      <div className='bo-bot-avatar' aria-hidden='true'>
                        <BiteAIAvatar />
                      </div>
                      <div className='message-bubble bo-thinking-bubble'>
                        {renderThinkingTranscript(message)}
                      </div>
                    </div>
                  ) : message.type === 'status' ? (
                    <div
                      key={message.id}
                      className={`chat-message bot bo-status-row${
                        message.variant ? ` bo-status-${message.variant}` : ''
                      }`}
                    >
                      <div className='message-bubble bo-status-message'>
                        {isStreaming &&
                        message.id === lastStatusMessageId &&
                        typeof message.text === 'string' &&
                        message.text.trimEnd().endsWith('...')
                          ? renderStreamingText(message.text)
                          : message.text}
                      </div>
                    </div>
                  ) : (
                    <div
                      key={message.id}
                      className={`chat-message ${message.sender}`}
                    >
                      {message.sender === 'bot' && (
                        <div className='bo-bot-avatar' aria-hidden='true'>
                          <BiteAIAvatar />
                        </div>
                      )}
                      <div
                        className={`message-bubble${
                          message.type === 'final'
                            ? ' bo-final-message'
                            : message.type === 'control' ||
                              message.type === 'agent_result' ||
                              message.type === 'layer_picklist' ||
                              message.type === 'jurisdiction_confirm'
                            ? ' bo-card-message'
                            : ''
                        }`}
                      >
                        {message.type === 'final'
                          ? renderFinalSummary(
                              message.data,
                              !isStreaming &&
                                messageIndex === messages.length - 1,
                            )
                          : message.type === 'control'
                          ? renderPauseControls(message)
                          : message.type === 'jurisdiction_confirm'
                          ? renderJurisdictionConfirm(message)
                          : message.type === 'layer_picklist'
                          ? renderLayerPicklist(message)
                          : message.type === 'agent_result'
                          ? renderAgentResult(message)
                          : renderStreamingText(message.text)}
                      </div>
                      {message.sender === 'user' && (
                        <div className='bo-profile-icon' aria-hidden='true'>
                          {cur_user.basics?.firstName?.[0] || ''}
                          {cur_user.basics?.lastName?.[0] || ''}
                        </div>
                      )}
                    </div>
                  ),
                )}
              </div>

              <WorkflowComposer
                inputRef={composerInputRef}
                value={input}
                onChange={setInput}
                onSubmit={handleSubmit}
                placeholder={
                  finalData
                    ? 'Ask a follow-up question or name another entity...'
                    : 'Type your message...'
                }
                disabled={isStreaming || !userHasAccess}
              />
            </>
          ) : (
            <div
              className='standard-workflow-empty-chat'
              style={{
                backgroundColor: 'var(--bg-colors)',
                color: 'var(--text-primary-color)',
                minHeight: 'calc(100vh - 114px)',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'center',
                width: '100%',
                padding: '24px 0',
                boxSizing: 'border-box',
                overflowY: 'auto',
                gap: '12px',
              }}
            >
              <WorkflowComposer
                inputRef={composerInputRef}
                value={input}
                onChange={setInput}
                onSubmit={handleSubmit}
                placeholder='Enter the entity name to investigate...'
                disabled={isStreaming || startBlockedByCredits || !userHasAccess}
              />
            </div>
          )}
        </section>

        <aside
          className='standard-workflow-progress-card'
          style={trialLimitReached ? { opacity: 0.55 } : undefined}
        >
          <WorkflowProgressHeader
            title='Investigation Progress'
            badge='Beta'
          />
          <WorkflowProgressTimeline steps={boProgressSteps} disableExpand />
        </aside>
      </div>
      <Modal open={isModalOpen} onClose={handleCloseModal}>
        <div>
          <CreateTicketWizard
            cur_user={cur_user}
            handleSaveTicket={saveWork}
            newTicketName={newTicketName}
            setNewTicketName={setNewTicketName}
            shareEmail={shareEmail}
            setShareEmail={setShareEmail}
            savingTicket={isSaving}
          />
        </div>
      </Modal>
      <Dialog
        className='dialog_box'
        open={isLeaveModalOpen}
        onClose={() => {
          setIsLeaveModalOpen(false);
          setPendingLocation(null);
        }}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: 'var(--bg-colors)',
            boxShadow:
              '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            borderRadius: '0.5rem',
            maxWidth: '620px',
            width: '90%',
            margin: '0 auto',
          },
        }}
      >
        <DialogTitle className='modal-header-content modal-title'>
          Leave Beneficial Ownership Workflow?
          <div
            className='audit-add-modal-head-cont-icon'
            onClick={() => {
              setIsLeaveModalOpen(false);
              setPendingLocation(null);
            }}
            style={{ cursor: 'pointer' }}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='18'
              height='18'
              viewBox='0 0 18 18'
              fill='none'
            >
              <path
                d='M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        </DialogTitle>
        <DialogContent className='dialog-content-base'>
          <div
            className='audit-add-modal-cont-body'
            style={{ paddingTop: '12px', paddingBottom: '12px' }}
          >
            <p className='wizard-body-text' style={{ marginBottom: '20px' }}>
              Leaving this workflow will discard your current work. Use Save
              Ticket to store this investigation in Saved Work before leaving.
            </p>
            <div
              style={{
                display: 'flex',
                gap: '10px',
                flexWrap: 'wrap',
                justifyContent: 'flex-end',
              }}
            >
              <button
                className='btn-new-clear'
                onClick={handleLeaveWithoutSaving}
                type='button'
              >
                <div>Leave Without Saving</div>
              </button>
              <button
                className='btn-new-automate'
                onClick={handleSaveTicketFromLeaveModal}
                type='button'
              >
                <div>Save Ticket</div>
              </button>
              <button
                className='btn-new-automate'
                onClick={handleLeaveAndOptOut}
                type='button'
              >
                <div>Leave + Don&apos;t Show Again</div>
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        className='dialog_box'
        open={notifyModalOpen}
        onClose={() => {
          setNotifyModalOpen(false);
          resetWorkflow();
        }}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: 'var(--bg-colors)',
            boxShadow:
              '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            borderRadius: '0.5rem',
            maxWidth: '620px',
            width: '90%',
            margin: '0 auto',
          },
        }}
      >
        <DialogTitle className='modal-header-content modal-title'>
          Running your investigation
        </DialogTitle>
        <DialogContent className='dialog-content-base'>
          <div
            className='audit-add-modal-cont-body'
            style={{ paddingTop: '12px', paddingBottom: '12px' }}
          >
            <p className='wizard-body-text' style={{ marginBottom: '20px' }}>
              Running it now — please expect an email from info@bitedata.io when
              the investigation is done.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                className='btn-new-automate'
                type='button'
                onClick={() => {
                  setNotifyModalOpen(false);
                  resetWorkflow();
                }}
              >
                <div>Okay</div>
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BeneficialOwnershipWorkflow;
